import { MacWindow } from './MacWindow';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import QRCode from 'qrcode';
import { ThreeDView } from './ThreeDView';
import { useSaaSContext } from '../../store/SaaSContext';
import { useThreeDContext, isWebGLAvailable } from '../../store/ThreeDContext';
import { useToast } from '../../store/ToastContext';
import { 
  Box, 
  X, 
  RotateCw, 
  RotateCcw,
  Sun, 
  Download, 
  Palette,
  FileCheck2,
  ZoomIn,
  ZoomOut,
  Focus,
  Film,
  Droplets,
  Layers,
  Sparkles,
  Printer,
  Sliders,
  Maximize2,
  LayoutGrid,
  Sparkle,
  Eye,
  CheckCircle2,
  Calendar,
  User,
  Info,
  MoveHorizontal,
  MoveVertical,
  Scaling,
  FlipHorizontal,
  CircleDot,
  ChevronDown,
  ChevronRight,
  ChevronsUp,
  ChevronsDown,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { ElementMaterialSetupPanel } from './ElementMaterialSetupPanel';
import { 
  renderTemplateMultiMapToCanvases, 
  projectCanvasToUvMap,
  createProportionalCanvas,
  MATERIAL_FINISH_PRESETS 
} from '../../utils/elementMaterialRenderer';
import { VdpElement } from '../../types';

interface ProofViewer3DModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OBJECT_MODELS = [
  { id: 'bottle', name: 'Glass Beverage Bottle', icon: '🍾', defaultCap: '#111827', defaultCapMat: 'metal_brushed', defaultBodyMat: 'glass_clear' },
  { id: 'can', name: 'Aluminum Soda/Beer Can', icon: '🥫', defaultCap: '#E2E8F0', defaultCapMat: 'metal_brushed', defaultBodyMat: 'metal_brushed' },
  { id: 'pouch', name: 'Stand-Up Foil Pouch', icon: '👝', defaultCap: '#F59E0B', defaultCapMat: 'plastic_matte', defaultBodyMat: 'plastic_matte' },
  { id: 'box', name: 'Product Shipping Box', icon: '📦', defaultCap: '#8B5CF6', defaultCapMat: 'plastic_matte', defaultBodyMat: 'plastic_matte' },
  { id: 'jar', name: 'Cosmetic Glass Jar', icon: '🫙', defaultCap: '#000000', defaultCapMat: 'plastic_glossy', defaultBodyMat: 'glass_frosted' },
  { id: 'cup', name: 'Coffee Cup / Tumbler', icon: '☕', defaultCap: '#EF4444', defaultCapMat: 'plastic_matte', defaultBodyMat: 'plastic_matte' }
];

export const CONTAINER_MATERIALS = [
  { id: 'plastic_matte', name: 'Matte Plastic', roughness: 0.85, metalness: 0.05, transmission: 0, opacity: 1 },
  { id: 'plastic_glossy', name: 'Glossy Plastic', roughness: 0.1, metalness: 0.05, transmission: 0, opacity: 1 },
  { id: 'glass_clear', name: 'Clear Glass', roughness: 0.05, metalness: 0.1, transmission: 0.98, opacity: 1, ior: 1.5, thickness: 0.5, envMapIntensity: 1.5 },
  { id: 'glass_frosted', name: 'Frosted Glass', roughness: 0.35, metalness: 0.1, transmission: 0.85, opacity: 1, ior: 1.5, thickness: 0.5, envMapIntensity: 1.0 },
  { id: 'glass_amber', name: 'Amber Glass', roughness: 0.05, metalness: 0.1, transmission: 0.95, opacity: 1, ior: 1.5, thickness: 0.5, envMapIntensity: 1.5, overrideColor: '#8a3311' },
  { id: 'metal_brushed', name: 'Brushed Metal', roughness: 0.4, metalness: 0.95, transmission: 0, opacity: 1, envMapIntensity: 1.2 },
  { id: 'metal_chrome', name: 'Chrome Metal', roughness: 0.05, metalness: 1.0, transmission: 0, opacity: 1, envMapIntensity: 2.0 },
];

export const MATERIAL_FINISHES = [
  { id: 'gloss', name: 'High-Gloss Clear Varnish', roughness: 0.08, metalness: 0.1, sheen: 'Ultra-Reflective Varnish' },
  { id: 'matte', name: 'Soft-Touch Velvet Matte', roughness: 0.85, metalness: 0.0, sheen: 'Zero-Glare Satin Finish' },
  { id: 'gold_foil', name: 'Metallic Gold Foil Stamp', roughness: 0.15, metalness: 0.95, color: '#F59E0B', sheen: 'Hot Stamp Metallic Sheen' },
  { id: 'silver_foil', name: 'Metallic Silver Foil Stamp', roughness: 0.12, metalness: 0.98, color: '#E2E8F0', sheen: 'Mirror Chrome Foil' },
  { id: 'clear_film', name: 'Clear Transparent Film', roughness: 0.1, metalness: 0.05, opacity: 0.85, sheen: 'No-Label Look' },
  { id: 'kraft', name: 'Textured Kraft Substrate', roughness: 0.92, metalness: 0.0, color: '#D97706', sheen: 'Organic Fiber Texture' }
];

export const BACKDROP_ENVIRONMENTS = [
  { id: 'studio_dark', name: 'Dark Studio Cyberpunk', icon: '🕋', floorColor: 0x1e293b, ringColor: 0xf59e0b, bg: 0x090d16 },
  { id: 'retail_shelf', name: 'Supermarket Retail Shelf', icon: '🛒', floorColor: 0x475569, ringColor: 0x10b981, bg: 0x0f172a },
  { id: 'marble_counter', name: 'Luxury Marble Countertop', icon: '🏛️', floorColor: 0xe2e8f0, ringColor: 0xd97706, bg: 0x111827 },
  { id: 'rustic_wood', name: 'Rustic Wooden Tabletop', icon: '🪵', floorColor: 0x78350f, ringColor: 0xfbbf24, bg: 0x1c1917 },
  { id: 'cyber_stage', name: 'Cyber Neon Stage', icon: '⚡', floorColor: 0x0284c7, ringColor: 0xec4899, bg: 0x030712 },
];

export const LIGHTING_PRESETS = [
  { id: 'studio_dark', name: 'Studio Dark Cyberpunk', bg: 0x090d16, ambient: 0.35, mainColor: 0xffffff, rimColor: 0xa855f7 },
  { id: 'luxury_white', name: 'Clean White Showroom', bg: 0x1e293b, ambient: 0.45, mainColor: 0xffffff, rimColor: 0x3b82f6 },
  { id: 'warm_sun', name: 'Warm Amber Sunlit', bg: 0x18100c, ambient: 0.35, mainColor: 0xfcb35f, rimColor: 0xec4899 },
  { id: 'neon_stage', name: 'Neon Retail Stage', bg: 0x0f0b1a, ambient: 0.35, mainColor: 0x06b6d4, rimColor: 0xd946ef }
];

export const CAMERA_ANGLES = [
  { id: 'isometric', name: '3/4 Isometric', dir: [1.0, 0.75, 1.1], baseDist: 4.8 },
  { id: 'front', name: 'Front', dir: [0, 0, 1], baseDist: 4.8 },
  { id: 'back', name: 'Back', dir: [0, 0, -1], baseDist: 4.8 },
  { id: 'left', name: 'Left', dir: [-1, 0, 0], baseDist: 4.8 },
  { id: 'right', name: 'Right', dir: [1, 0, 0], baseDist: 4.8 },
  { id: 'top', name: 'Top-Down', dir: [0, 1.4, 0.9], baseDist: 4.8 },
  { id: 'macro', name: 'Macro Close-Up', dir: [0, 0.1, 1], baseDist: 2.2 }
];

export const COLOR_SWATCHES = [
  '#ffffff', '#111827', '#334155', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#d97706', '#0284c7'
];

// Helper: Procedural Chilled Condensation Water Droplet Map
function createCondensationDropletsTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 1024; canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, 1024, 1024);
    
    // 1. Micro-condensation mist specks
    for (let i = 0; i < 1500; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const r = 1.0 + Math.random() * 2.2;
      ctx.fillStyle = '#d0d0d0';
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // 2. Medium rounded droplets
    for (let i = 0; i < 500; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const r = 3 + Math.random() * 9;
      
      const grad = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 0.5, x, y, r);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.7, '#b0b0b0');
      grad.addColorStop(1, '#606060');
      
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // 3. Heavy dripping liquid droplets with vertical gravity trails
    for (let i = 0; i < 70; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const r = 6 + Math.random() * 12;
      const dripLen = 20 + Math.random() * 50;

      // Drip trail
      const trailGrad = ctx.createLinearGradient(x, y, x, y + dripLen);
      trailGrad.addColorStop(0, '#ffffff');
      trailGrad.addColorStop(1, '#808080');
      ctx.fillStyle = trailGrad;
      ctx.beginPath();
      ctx.ellipse(x, y + dripLen / 2, r * 0.4, dripLen / 2, 0, 0, Math.PI * 2);
      ctx.fill();

      // Main heavy droplet bulb
      const grad = ctx.createRadialGradient(x - r * 0.25, y + dripLen - r * 0.25, 0.5, x, y + dripLen, r);
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(0.6, '#c0c0c0');
      grad.addColorStop(1, '#606060');

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(x, y + dripLen, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  return tex;
}

// Helper: Procedural 3D Tactile Relief / Height Map Shader Bump Texture
function createReliefBumpTexture(
  sourceCanvas: HTMLCanvasElement, 
  mode: string, 
  targetArea: string, 
  template: any, 
  csvData: any, 
  edgeSoftness: number
): THREE.CanvasTexture {
  const bumpCanvas = document.createElement('canvas');
  bumpCanvas.width = sourceCanvas.width;
  bumpCanvas.height = sourceCanvas.height;
  const ctx = bumpCanvas.getContext('2d');

  if (ctx) {
    // Fill neutral mid-gray (128 = 0 height displacement bias in Three.js shader)
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, bumpCanvas.width, bumpCanvas.height);

    let activeSource: HTMLCanvasElement = sourceCanvas;

    // Filter target design area if requested
    if (targetArea === 'text_logo' || targetArea === 'graphics') {
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = sourceCanvas.width;
      maskCanvas.height = sourceCanvas.height;
      
      const filteredElements = (template?.elements || []).filter((el: any) => {
        if (targetArea === 'text_logo') {
          return ['text', 'currency', 'date', 'serial', 'uppercase', 'qr', 'barcode', 'ean13', 'upca'].includes(el.type);
        } else if (targetArea === 'graphics') {
          return ['rectangle', 'circle', 'image'].includes(el.type);
        }
        return true;
      });

      renderTemplateToCanvas(maskCanvas, { ...template, elements: filteredElements, backgroundColor: 'transparent' }, csvData);
      activeSource = maskCanvas;
    }

    if (edgeSoftness > 0) {
      ctx.filter = `blur(${Math.min(6, edgeSoftness * 1.5)}px)`;
    }
    ctx.drawImage(activeSource, 0, 0);
    ctx.filter = 'none';

    try {
      const imgData = ctx.getImageData(0, 0, bumpCanvas.width, bumpCanvas.height);
      const data = imgData.data;

      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const lum = r * 0.299 + g * 0.587 + b * 0.114;

        let isTarget = false;
        if (targetArea === 'high_contrast') {
          isTarget = a > 20 && lum < 150;
        } else {
          isTarget = a > 15;
        }

        let val = 128; // neutral 0 displacement height
        if (isTarget) {
          if (mode === 'embossed' || mode === 'spot_uv') {
            // Extruded height (bright white = +3D height map extrusion)
            val = Math.min(255, 200 + Math.round((255 - lum) * 0.22));
          } else if (mode === 'debossed') {
            // Indented height (dark black = -3D height map debossing)
            val = Math.max(0, 50 - Math.round(lum * 0.18));
          }
        }

        data[i] = val;
        data[i + 1] = val;
        data[i + 2] = val;
        data[i + 3] = 255;
      }
      ctx.putImageData(imgData, 0, 0);
    } catch (bumpErr) {
      console.warn('Could not compute tactile relief bump map:', bumpErr);
    }
  }

  const tex = new THREE.CanvasTexture(bumpCanvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// Helper: Render Back Label (Renders active user design template for 100% consistency)
function renderBackLabelToCanvas(canvas: HTMLCanvasElement, title: string, template?: any, csvData?: any) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (template && template.elements && template.elements.length > 0) {
    renderTemplateToCanvas(canvas, template, csvData);
    return;
  }

  const w = canvas.width;
  const h = canvas.height;
  
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  
  ctx.fillStyle = '#0f172a';
  ctx.font = `bold ${h * 0.08}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(title || 'PRODUCT SPECIFICATIONS', w / 2, h * 0.15);
  
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(w * 0.1, h * 0.22);
  ctx.lineTo(w * 0.9, h * 0.22);
  ctx.stroke();
  
  ctx.font = `${h * 0.045}px sans-serif`;
  ctx.textAlign = 'left';
  const facts = [
    'Serving Size: 1 Container (355mL)',
    'Amount Per Serving:',
    'Calories: 110 • Fat: 0g',
    'Sodium: 15mg (1% DV)',
    'Total Carb: 28g (10% DV)',
    'Sugars: 26g (52% DV)',
    'Protein: 0g',
    'Ingredients: Purified Water, Organic Cane Juice, Natural Flavor, Citric Acid.'
  ];
  facts.forEach((line, idx) => {
    ctx.fillText(line, w * 0.12, h * 0.3 + idx * (h * 0.065));
  });
  
  // Barcode
  ctx.fillStyle = '#000000';
  const barY = h * 0.8;
  const barH = h * 0.12;
  for (let i = 0; i < 30; i++) {
    if (i % 3 !== 1) {
      ctx.fillRect(w * 0.2 + i * (w * 0.02), barY, w * 0.015, barH);
    }
  }
}

// Helper: Render Neck Label (Seal Ring Logo)
function renderNeckLabelToCanvas(canvas: HTMLCanvasElement, text: string) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(0, 0, w, h);
  
  ctx.fillStyle = '#000000';
  ctx.font = `bold ${h * 0.35}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(text || 'PREMIUM QUALITY • SEAL OF EXCELLENCE', w / 2, h * 0.65);
}

function renderTemplateToCanvas(canvas: HTMLCanvasElement, template: any, csvData: any) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const width = canvas.width;
  const height = canvas.height;

  const scaleX = width / (template.width || 100);
  const scaleY = height / (template.height || 100);

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = template.backgroundColor || template.bgColor || '#ffffff';
  ctx.fillRect(0, 0, width, height);

  const sortedElements = [...(template.elements || [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  sortedElements.forEach(el => {
    ctx.save();

    const x = (el.x || 0) * scaleX;
    const y = (el.y || 0) * scaleY;
    const w = (el.width || 40) * scaleX;
    const h = (el.height || 40) * scaleY;

    if (el.rotation) {
      ctx.translate(x + w / 2, y + h / 2);
      ctx.rotate((el.rotation * Math.PI) / 180);
      ctx.translate(-(x + w / 2), -(y + h / 2));
    }

    if (el.opacity !== undefined && !isNaN(Number(el.opacity))) {
      ctx.globalAlpha = Number(el.opacity);
    }

    const fallbackText = el.content ?? el.text ?? el.staticValue ?? el.value ?? el.name ?? '';
    let val = fallbackText;

    if (el.columnName && el.columnName.trim() !== '') {
      const colName = el.columnName.trim();
      if (csvData && csvData.rows && csvData.rows[0] && csvData.rows[0][colName]) {
        val = csvData.rows[0][colName];
      } else if (!fallbackText) {
        val = `[${colName}]`;
      }
    }
    if (!val && el.type === 'text') {
      val = 'Sample Text';
    }

    if (['text', 'currency', 'date', 'serial', 'uppercase'].includes(el.type) && typeof val === 'string') {
      if (el.textCase === 'uppercase' || el.type === 'uppercase') {
        val = val.toUpperCase();
      } else if (el.textCase === 'lowercase') {
        val = val.toLowerCase();
      } else if (el.textCase === 'capitalize') {
        val = val.replace(/\b\w/g, c => c.toUpperCase());
      }
      if (el.prefix) val = `${el.prefix}${val}`;
      if (el.suffix) val = `${val}${el.suffix}`;
    }

    if (['text', 'currency', 'date', 'serial', 'uppercase'].includes(el.type)) {
      const fontSize = (el.fontSize || 14) * scaleY;
      const fontFamily = el.fontFamily || 'Helvetica, Arial, sans-serif';
      const fontStyle = el.isItalic ? 'italic' : 'normal';
      const fontWeight = el.isBold ? 'bold' : 'normal';

      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.fillStyle = el.color || '#000000';
      ctx.textAlign = (el.textAlign as CanvasTextAlign) || 'left';
      ctx.textBaseline = 'top';

      let textX = x;
      if (el.textAlign === 'center') textX = x + w / 2;
      else if (el.textAlign === 'right') textX = x + w;

      if (el.type === 'currency' && Number(val)) {
        val = `${el.currencySymbol || '$'}${Number(val).toFixed(2)}`;
      }

      ctx.fillText(String(val), textX, y);
    } else if (el.type === 'rectangle') {
      ctx.fillStyle = el.color || el.backgroundColor || '#3b82f6';
      ctx.fillRect(x, y, w, h);
    } else if (el.type === 'circle') {
      ctx.fillStyle = el.color || el.backgroundColor || '#3b82f6';
      ctx.beginPath();
      ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI);
      ctx.fill();
    } else if (el.type === 'qr') {
      try {
        const ecl = (['L', 'M', 'Q', 'H'].includes(el.qrErrorCorrection || '') ? el.qrErrorCorrection : 'M') as 'L' | 'M' | 'Q' | 'H';
        const qrData = QRCode.create(val || 'https://example.com', { errorCorrectionLevel: ecl });
        const size = qrData.modules.size;
        const modules = qrData.modules.data;
        const cellW = w / size;
        const cellH = h / size;

        if (!el.isTransparentBg) {
          ctx.fillStyle = el.backgroundColor || '#ffffff';
          ctx.fillRect(x, y, w, h);
        }
        ctx.fillStyle = el.color || '#000000';
        for (let r = 0; r < size; r++) {
          for (let c = 0; c < size; c++) {
            if (modules[r * size + c] === 1) {
              ctx.fillRect(x + c * cellW, y + r * cellH, cellW + 0.1, cellH + 0.1);
            }
          }
        }
      } catch (e) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x, y, w, h);
      }
    } else if (el.type === 'barcode' || el.type === 'ean13' || el.type === 'upca') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = el.color || '#0f172a';
      const bars = 24;
      const barW = w / bars;
      for (let i = 0; i < bars; i++) {
        if (i % 3 !== 1) {
          ctx.fillRect(x + i * barW, y, barW * 0.8, h * 0.7);
        }
      }
      ctx.font = `${h * 0.22}px monospace`;
      ctx.textAlign = 'center';
      ctx.fillText(val || '123456789', x + w / 2, y + h * 0.75);
    } else if (el.type === 'image') {
      if (el.src || el.staticValue) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          ctx.drawImage(img, x, y, w, h);
        };
        img.src = el.src || el.staticValue || '';
      } else {
        ctx.fillStyle = '#f1f5f9';
        ctx.fillRect(x, y, w, h);
      }
    }

    ctx.restore();
  });
}

export const ProofViewer3DModal: React.FC<ProofViewer3DModalProps> = ({ isOpen, onClose }) => {
  const { template, csvData, setTemplate } = useSaaSContext();
  const threeD = useThreeDContext();
  const { toast } = useToast();

  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const proofContainerRef = useRef<HTMLDivElement | null>(null);
  const [isEngineReady, setIsEngineReady] = useState<boolean>(false);
  const [highlightActiveElement, setHighlightActiveElement] = useState<boolean>(false);

  const handleUpdateElementInTemplate = (elementId: string, updates: Partial<VdpElement>) => {
    if (!template || !template.elements) return;
    setTemplate({
      ...template,
      elements: template.elements.map(el => el.id === elementId ? { ...el, ...updates } : el)
    });
  };

  const setMountRef = useCallback((node: HTMLDivElement | null) => {
    mountRef.current = node;
    (proofContainerRef as any).current = node;
    setContainerNode(node);
  }, []);

  // Centralized 3D Studio States
  const selectedModel = threeD.selectedModel;
  const setSelectedModel = (val: any) => threeD.update3DState({ selectedModel: typeof val === 'function' ? val(threeD.selectedModel) : val });

  const selectedFinish = threeD.selectedFinish;
  const setSelectedFinish = (val: any) => threeD.update3DState({ selectedFinish: typeof val === 'function' ? val(threeD.selectedFinish) : val });

  const lightingPreset = threeD.lightingPreset;
  const setLightingPreset = (val: any) => threeD.update3DState({ lightingPreset: typeof val === 'function' ? val(threeD.lightingPreset) : val });

  const backdropEnv = threeD.backdropEnv;
  const setBackdropEnv = (val: any) => threeD.update3DState({ backdropEnv: typeof val === 'function' ? val(threeD.backdropEnv) : val });

  const cameraAngle = threeD.cameraAngle;
  const setCameraAngle = (val: any) => threeD.update3DState({ cameraAngle: typeof val === 'function' ? val(threeD.cameraAngle) : val });

  const [activeTab, setActiveTab] = useState<'all' | 'elementMaterials' | 'substrate' | 'uv' | 'light' | 'environment' | 'finish' | 'multilabel'>('all');
  
  // Collapsible Panel Accordion State
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    elementMaterials: true, // Section: Particular Element Finishes
    substrate: false,       // Section 1: Substrate & Material
    uv: false,              // Section 2: Sticker Placement & UV Mapping
    light: false,           // Section 3: Studio Light Setup
    environment: false,     // Section 4: Stage & Backdrops
    finish: false,          // Section 5: Finish & Selective Foil
    relief: false,          // Section 6: Tactile Embossing
    multilabel: false,      // Section 7: Multi-Label & Condensation
  });

  const toggleSection = (sectionKey: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const expandAllSections = () => {
    setOpenSections({
      elementMaterials: true,
      substrate: true,
      uv: true,
      light: true,
      environment: true,
      finish: true,
      relief: true,
      multilabel: true,
    });
  };

  const collapseAllSections = () => {
    setOpenSections({
      elementMaterials: false,
      substrate: false,
      uv: false,
      light: false,
      environment: false,
      finish: false,
      relief: false,
      multilabel: false,
    });
  };
  
  // Customization & Interactive Controls
  const [zoomLevel, setZoomLevel] = useState<number>(1.0);
  const autoRotate = threeD.autoRotate;
  const setAutoRotate = (val: any) => threeD.update3DState({ autoRotate: typeof val === 'function' ? val(threeD.autoRotate) : val });

  const turntableSpeed = threeD.turntableSpeed;
  const setTurntableSpeed = (val: any) => threeD.update3DState({ turntableSpeed: typeof val === 'function' ? val(threeD.turntableSpeed) : val });

  const autoRotateRef = useRef<boolean>(autoRotate);
  useEffect(() => { autoRotateRef.current = autoRotate; }, [autoRotate]);
  const turntableSpeedRef = useRef<number>(turntableSpeed);
  useEffect(() => { turntableSpeedRef.current = turntableSpeed; }, [turntableSpeed]);

  // UV Mapping & Texture Mapping Settings
  const uvScale = threeD.uvScale;
  const setUvScale = (val: any) => threeD.update3DState({ uvScale: typeof val === 'function' ? val(threeD.uvScale) : val });

  const uvX = threeD.uvX;
  const setUvX = (val: any) => threeD.update3DState({ uvX: typeof val === 'function' ? val(threeD.uvX) : val });

  const uvY = threeD.uvY;
  const setUvY = (val: any) => threeD.update3DState({ uvY: typeof val === 'function' ? val(threeD.uvY) : val });

  const uvRotation = threeD.uvRotation;
  const setUvRotation = (val: any) => threeD.update3DState({ uvRotation: typeof val === 'function' ? val(threeD.uvRotation) : val });

  const uvRepeatX = threeD.uvRepeatX;
  const setUvRepeatX = (val: any) => threeD.update3DState({ uvRepeatX: typeof val === 'function' ? val(threeD.uvRepeatX) : val });

  const uvRepeatY = threeD.uvRepeatY;
  const setUvRepeatY = (val: any) => threeD.update3DState({ uvRepeatY: typeof val === 'function' ? val(threeD.uvRepeatY) : val });

  const wrapAround = threeD.wrapAround;
  const setWrapAround = (val: any) => threeD.update3DState({ wrapAround: typeof val === 'function' ? val(threeD.wrapAround) : val });

  const boxStickerFace = threeD.boxStickerFace;
  const setBoxStickerFace = (val: any) => threeD.update3DState({ boxStickerFace: typeof val === 'function' ? val(threeD.boxStickerFace) : val });

  const stickerOffsetZ = threeD.stickerOffsetZ;
  const setStickerOffsetZ = (val: any) => threeD.update3DState({ stickerOffsetZ: typeof val === 'function' ? val(threeD.stickerOffsetZ) : val });

  // Materials & Custom Surface Parameters
  const capColor = threeD.capColor;
  const setCapColor = (val: any) => threeD.update3DState({ capColor: typeof val === 'function' ? val(threeD.capColor) : val });

  const capMaterialId = threeD.capMaterialId;
  const setCapMaterialId = (val: any) => threeD.update3DState({ capMaterialId: typeof val === 'function' ? val(threeD.capMaterialId) : val });

  const containerColor = threeD.containerColor;
  const setContainerColor = (val: any) => threeD.update3DState({ containerColor: typeof val === 'function' ? val(threeD.containerColor) : val });

  const containerMaterial = threeD.containerMaterial;
  const setContainerMaterial = (val: any) => threeD.update3DState({ containerMaterial: typeof val === 'function' ? val(threeD.containerMaterial) : val });

  const selectiveFinishColor = threeD.selectiveFinishColor;
  const setSelectiveFinishColor = (val: any) => threeD.update3DState({ selectiveFinishColor: typeof val === 'function' ? val(threeD.selectiveFinishColor) : val });

  const useCustomMaterial = threeD.useCustomMaterial;
  const setUseCustomMaterial = (val: any) => threeD.update3DState({ useCustomMaterial: typeof val === 'function' ? val(threeD.useCustomMaterial) : val });

  const containerRoughness = threeD.containerRoughness;
  const setContainerRoughness = (val: any) => threeD.update3DState({ containerRoughness: typeof val === 'function' ? val(threeD.containerRoughness) : val });

  const containerMetalness = threeD.containerMetalness;
  const setContainerMetalness = (val: any) => threeD.update3DState({ containerMetalness: typeof val === 'function' ? val(threeD.containerMetalness) : val });

  const containerTransmission = threeD.containerTransmission;
  const setContainerTransmission = (val: any) => threeD.update3DState({ containerTransmission: typeof val === 'function' ? val(threeD.containerTransmission) : val });

  const containerIor = threeD.containerIor;
  const setContainerIor = (val: any) => threeD.update3DState({ containerIor: typeof val === 'function' ? val(threeD.containerIor) : val });

  // Light Setup Customization
  const lightIntensity = threeD.lightIntensity;
  const setLightIntensity = (val: any) => threeD.update3DState({ lightIntensity: typeof val === 'function' ? val(threeD.lightIntensity) : val });

  const lightColor = threeD.lightColor;
  const setLightColor = (val: any) => threeD.update3DState({ lightColor: typeof val === 'function' ? val(threeD.lightColor) : val });

  const ambientIntensity = threeD.ambientIntensity;
  const setAmbientIntensity = (val: any) => threeD.update3DState({ ambientIntensity: typeof val === 'function' ? val(threeD.ambientIntensity) : val });

  const ambientLightEnabled = threeD.ambientLightEnabled ?? true;
  const setAmbientLightEnabled = (val: any) => threeD.update3DState({ ambientLightEnabled: typeof val === 'function' ? val(threeD.ambientLightEnabled ?? true) : val });

  const rimLightIntensity = threeD.rimLightIntensity;
  const setRimLightIntensity = (val: any) => threeD.update3DState({ rimLightIntensity: typeof val === 'function' ? val(threeD.rimLightIntensity) : val });

  const rimLightColor = threeD.rimLightColor;
  const setRimLightColor = (val: any) => threeD.update3DState({ rimLightColor: typeof val === 'function' ? val(threeD.rimLightColor) : val });

  const lightAngleX = threeD.lightAngleX;
  const setLightAngleX = (val: any) => threeD.update3DState({ lightAngleX: typeof val === 'function' ? val(threeD.lightAngleX) : val });

  const lightAngleY = threeD.lightAngleY;
  const setLightAngleY = (val: any) => threeD.update3DState({ lightAngleY: typeof val === 'function' ? val(threeD.lightAngleY) : val });

  // Environment & Studio Stage Backdrops
  const toneMappingExposure = threeD.toneMappingExposure;
  const setToneMappingExposure = (val: any) => threeD.update3DState({ toneMappingExposure: typeof val === 'function' ? val(threeD.toneMappingExposure) : val });

  const pedestalColor = threeD.pedestalColor;
  const setPedestalColor = (val: any) => threeD.update3DState({ pedestalColor: typeof val === 'function' ? val(threeD.pedestalColor) : val });

  const pedestalRingColor = threeD.pedestalRingColor;
  const setPedestalRingColor = (val: any) => threeD.update3DState({ pedestalRingColor: typeof val === 'function' ? val(threeD.pedestalRingColor) : val });

  const pedestalVisible = threeD.pedestalVisible;
  const setPedestalVisible = (val: any) => threeD.update3DState({ pedestalVisible: typeof val === 'function' ? val(threeD.pedestalVisible) : val });

  // Advanced 3D Features State
  const showCondensation = threeD.showCondensation;
  const setShowCondensation = (val: any) => threeD.update3DState({ showCondensation: typeof val === 'function' ? val(threeD.showCondensation) : val });

  const embossMode = threeD.embossMode;
  const setEmbossMode = (val: any) => threeD.update3DState({ embossMode: typeof val === 'function' ? val(threeD.embossMode) : val });

  const reliefHeight = threeD.reliefHeight;
  const setReliefHeight = (val: any) => threeD.update3DState({ reliefHeight: typeof val === 'function' ? val(threeD.reliefHeight) : val });

  const tactileTargetArea = threeD.tactileTargetArea;
  const setTactileTargetArea = (val: any) => threeD.update3DState({ tactileTargetArea: typeof val === 'function' ? val(threeD.tactileTargetArea) : val });

  const edgeSoftness = threeD.edgeSoftness;
  const setEdgeSoftness = (val: any) => threeD.update3DState({ edgeSoftness: typeof val === 'function' ? val(threeD.edgeSoftness) : val });

  const enableMultiLabel = threeD.enableMultiLabel;
  const setEnableMultiLabel = (val: any) => threeD.update3DState({ enableMultiLabel: typeof val === 'function' ? val(threeD.enableMultiLabel) : val });

  const backLabelTitle = threeD.backLabelTitle;
  const setBackLabelTitle = (val: any) => threeD.update3DState({ backLabelTitle: typeof val === 'function' ? val(threeD.backLabelTitle) : val });

  const neckLabelText = threeD.neckLabelText;
  const setNeckLabelText = (val: any) => threeD.update3DState({ neckLabelText: typeof val === 'function' ? val(threeD.neckLabelText) : val });

  // 360 Spin Recorder
  const [isRecording360, setIsRecording360] = useState<boolean>(false);
  const [spinSnapshots, setSpinSnapshots] = useState<string[]>([]);
  const [showSpinGallery, setShowSpinGallery] = useState<boolean>(false);
  const [galleryFrameIndex, setGalleryFrameIndex] = useState<number>(0);

  // Official Printable PDF Proof Certificate Modal
  const [showPdfProofModal, setShowPdfProofModal] = useState<boolean>(false);
  const [proofSnapshotUrl, setProofSnapshotUrl] = useState<string>('');
  const [proofQrCodeUrl, setProofQrCodeUrl] = useState<string>('');

  // Proof Metadata
  const [clientName, setClientName] = useState<string>('Acme Premium Brands');
  const [approvalStatus, setApprovalStatus] = useState<'AWAITING' | 'APPROVED' | 'REVISION'>('AWAITING');
  const [clientNotes, setClientNotes] = useState<string>('Check foil sheen and text clarity before production run.');

  // References for threejs objects
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const meshRef = useRef<THREE.Object3D | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const mainSpotRef = useRef<THREE.SpotLight | null>(null);
  const lightsGroupRef = useRef<THREE.Group | null>(null);
  const pedestalGroupRef = useRef<THREE.Group | null>(null);
  const meshRotationRef = useRef<{x: number, y: number}>({x: 0, y: 0});
  const activeTexturesRef = useRef<THREE.Texture[]>([]);

  // 1. WebGL Engine & Canvas Lifecycle Management Hook
  useEffect(() => {
    if (!isOpen || !containerNode) {
      setIsEngineReady(false);
      return;
    }

    console.log('[3D Studio] Initializing WebGL engine lifecycle on mounted container...');
    threeD.setSceneLifecycle('mounting');
    threeD.setEngineStatus('initializing');

    if (!isWebGLAvailable()) {
      console.error('[3D Studio] WebGL hardware acceleration is not supported or disabled.');
      threeD.setEngineStatus('error', 'WebGL is not supported or graphics hardware acceleration is disabled in your browser.');
      threeD.setSceneLifecycle('disposed');
      setIsEngineReady(false);
      return;
    }

    const rect = containerNode.getBoundingClientRect();
    const width = Math.max(300, Math.floor(rect.width || containerNode.clientWidth || 800));
    const height = Math.max(300, Math.floor(rect.height || containerNode.clientHeight || 500));

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 1000);
    cameraRef.current = camera;

    const selectedCamAngle = CAMERA_ANGLES.find(c => c.id === cameraAngle) || CAMERA_ANGLES[0];
    const dirVec = new THREE.Vector3(selectedCamAngle.dir[0], selectedCamAngle.dir[1], selectedCamAngle.dir[2]).normalize();
    const currentDist = (selectedCamAngle.baseDist || 4.8) / zoomLevel;
    camera.position.set(dirVec.x * currentDist, dirVec.y * currentDist, dirVec.z * currentDist);
    camera.lookAt(0, 0, 0);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance',
        alpha: true
      });
      rendererRef.current = renderer;
    } catch (err: any) {
      console.error('[3D Studio] WebGLRenderer creation error:', err);
      threeD.setEngineStatus('error', `WebGL context creation failed: ${err?.message || 'Check GPU acceleration.'}`);
      threeD.setSceneLifecycle('disposed');
      setIsEngineReady(false);
      return;
    }

    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = toneMappingExposure;
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.objectFit = 'contain';
    renderer.domElement.style.display = 'block';

    const handleContextLost = (e: Event) => {
      e.preventDefault();
      console.warn('[3D Studio] WebGL Context Lost!');
      threeD.setEngineStatus('context_lost', 'GPU WebGL context was lost. Click Retry to re-initialize.');
      threeD.setSceneLifecycle('paused');
    };

    const handleContextRestored = () => {
      console.log('[3D Studio] WebGL Context Restored!');
      threeD.retryEngineInit();
    };

    renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false);
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored, false);

    try {
      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      const envTexture = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
      scene.environment = envTexture;
      pmremGenerator.dispose();
    } catch (pmremErr) {
      console.warn('[3D Studio] RoomEnvironment PMREM fallback:', pmremErr);
    }

    containerNode.innerHTML = '';
    containerNode.appendChild(renderer.domElement);
    threeD.setActiveCanvas(renderer.domElement);

    const handleResize = () => {
      if (!containerNode || !rendererRef.current || !cameraRef.current) return;
      const r = containerNode.getBoundingClientRect();
      const w = Math.floor(r.width || containerNode.clientWidth);
      const h = Math.floor(r.height || containerNode.clientHeight);
      if (w > 0 && h > 0) {
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(w, h, false);
        rendererRef.current.domElement.style.width = '100%';
        rendererRef.current.domElement.style.height = '100%';
        rendererRef.current.domElement.style.objectFit = 'contain';
        rendererRef.current.domElement.style.display = 'block';
      }
    };
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerNode);
    handleResize();
    const initialResizeTimer = setTimeout(handleResize, 60);

    // Interactive Mouse Orbit Controls
    let isDragging = false;
    let prevMousePos = { x: 0, y: 0 };
    const dom = renderer.domElement;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      prevMousePos = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !meshRef.current) return;
      const deltaX = e.clientX - prevMousePos.x;
      const deltaY = e.clientY - prevMousePos.y;

      meshRef.current.rotation.y += deltaX * 0.01;
      meshRef.current.rotation.x += deltaY * 0.008;
      meshRotationRef.current.x = meshRef.current.rotation.x;
      meshRotationRef.current.y = meshRef.current.rotation.y;

      prevMousePos = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => { isDragging = false; };
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomDelta = e.deltaY < 0 ? 0.12 : -0.12;
      setZoomLevel(prev => Math.min(3.0, Math.max(0.4, Number((prev + zoomDelta).toFixed(2)))));
    };

    dom.addEventListener('mousedown', handleMouseDown);
    dom.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    let animId: number;
    let isTerminated = false;

    const animate = () => {
      if (isTerminated) return;
      animId = requestAnimationFrame(animate);

      if (autoRotateRef.current && !isDragging && meshRef.current) {
        meshRef.current.rotation.y += 0.005 * turntableSpeedRef.current;
        meshRotationRef.current.y = meshRef.current.rotation.y;
      }

      try {
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
          rendererRef.current.render(sceneRef.current, cameraRef.current);
        }
      } catch (e) {
        console.error('[3D Studio] Render loop error:', e);
      }
    };

    animate();

    threeD.setEngineStatus('ready');
    threeD.setSceneLifecycle('active');
    setIsEngineReady(true);

    return () => {
      isTerminated = true;
      cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      clearTimeout(initialResizeTimer);
      dom.removeEventListener('mousedown', handleMouseDown);
      dom.removeEventListener('wheel', handleWheel);
      dom.removeEventListener('webglcontextlost', handleContextLost);
      dom.removeEventListener('webglcontextrestored', handleContextRestored);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      if (containerNode && renderer.domElement) {
        try {
          containerNode.removeChild(renderer.domElement);
        } catch (e) {}
      }

      if (sceneRef.current) {
        sceneRef.current.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const m = child as THREE.Mesh;
            if (m.geometry) m.geometry.dispose();
            if (Array.isArray(m.material)) {
              m.material.forEach(mat => mat.dispose());
            } else if (m.material) {
              m.material.dispose();
            }
          }
        });
        sceneRef.current.clear();
      }

      renderer.dispose();
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
      meshRef.current = null;
      threeD.setActiveCanvas(null);
      threeD.setSceneLifecycle('disposed');
      setIsEngineReady(false);
    };
  }, [isOpen, containerNode, threeD.engineInitVersion]);

  // 2. Lighting & Backdrop Environment Rig Update
  useEffect(() => {
    if (!isEngineReady || !sceneRef.current || !rendererRef.current) return;
    const scene = sceneRef.current;
    const renderer = rendererRef.current;

    // Update backdrop background color & tone mapping exposure
    const currentBackdrop = BACKDROP_ENVIRONMENTS.find(b => b.id === backdropEnv) || BACKDROP_ENVIRONMENTS[0];
    scene.background = new THREE.Color(currentBackdrop.bg);
    renderer.toneMappingExposure = toneMappingExposure;

    // Update Lighting Rig
    if (lightsGroupRef.current) {
      scene.remove(lightsGroupRef.current);
    }
    const lightsGroup = new THREE.Group();
    lightsGroupRef.current = lightsGroup;

    // Ambient fill light (toggleable for high-contrast dramatic studio vs uniform soft fill)
    if (ambientLightEnabled) {
      const ambientLight = new THREE.AmbientLight(0xffffff, Math.max(0.08, ambientIntensity * 0.4));
      lightsGroup.add(ambientLight);

      const fillLight = new THREE.DirectionalLight(new THREE.Color(lightColor), Math.max(0.05, ambientIntensity * 0.45));
      fillLight.position.set(-5, 4, 5);
      lightsGroup.add(fillLight);
    }

    // Realistic keylight with natural falloff decay and calibrated intensity
    const mainSpot = new THREE.SpotLight(new THREE.Color(lightColor), Math.max(0.2, lightIntensity * 1.2));
    mainSpotRef.current = mainSpot;
    mainSpot.position.set(lightAngleX, lightAngleY, 7);
    mainSpot.angle = Math.PI / 3.5;
    mainSpot.penumbra = 0.6;
    mainSpot.decay = 1.0;
    mainSpot.castShadow = true;
    mainSpot.target.position.set(0, 0, 0);
    lightsGroup.add(mainSpot);
    lightsGroup.add(mainSpot.target);

    const rimLight = new THREE.DirectionalLight(new THREE.Color(rimLightColor), Math.max(0.08, rimLightIntensity * 0.6));
    rimLight.position.set(-6, 3, -6);
    lightsGroup.add(rimLight);

    scene.add(lightsGroup);
  }, [
    isEngineReady, backdropEnv, toneMappingExposure, ambientLightEnabled, ambientIntensity, 
    lightColor, lightIntensity, lightAngleX, lightAngleY, rimLightColor, rimLightIntensity
  ]);

  // 3. Pedestal Stage Update
  useEffect(() => {
    if (!isEngineReady || !sceneRef.current) return;
    const scene = sceneRef.current;

    const currentBackdrop = BACKDROP_ENVIRONMENTS.find(b => b.id === backdropEnv) || BACKDROP_ENVIRONMENTS[0];

    if (pedestalGroupRef.current) {
      scene.remove(pedestalGroupRef.current);
      pedestalGroupRef.current.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) {
          const m = c as THREE.Mesh;
          if (m.geometry) m.geometry.dispose();
          if (Array.isArray(m.material)) m.material.forEach(mat => mat.dispose());
          else if (m.material) m.material.dispose();
        }
      });
      pedestalGroupRef.current = null;
    }

    if (pedestalVisible) {
      const pGroup = new THREE.Group();
      pedestalGroupRef.current = pGroup;

      const activeFloorHex = pedestalColor || ('#' + currentBackdrop.floorColor.toString(16).padStart(6, '0'));
      const activeRingHex = pedestalRingColor || ('#' + currentBackdrop.ringColor.toString(16).padStart(6, '0'));

      const floorGeo = new THREE.CylinderGeometry(2.5, 2.7, 0.15, 64);
      const floorMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(activeFloorHex),
        roughness: backdropEnv === 'marble_counter' ? 0.08 : (backdropEnv === 'rustic_wood' ? 0.7 : 0.3),
        metalness: backdropEnv === 'cyber_stage' ? 0.8 : (backdropEnv === 'marble_counter' ? 0.2 : 0.6)
      });
      const pedestal = new THREE.Mesh(floorGeo, floorMat);
      pedestal.position.set(0, -1.6, 0);
      pedestal.receiveShadow = true;
      pGroup.add(pedestal);

      const ringGeo = new THREE.TorusGeometry(2.52, 0.03, 16, 64);
      const ringMat = new THREE.MeshStandardMaterial({ 
        color: new THREE.Color(activeRingHex), 
        metalness: 0.9, 
        roughness: 0.1,
        emissive: new THREE.Color(activeRingHex),
        emissiveIntensity: backdropEnv === 'cyber_stage' ? 0.8 : 0.3
      });
      const ringMesh = new THREE.Mesh(ringGeo, ringMat);
      ringMesh.rotation.x = Math.PI / 2;
      ringMesh.position.set(0, -1.52, 0);
      pGroup.add(ringMesh);

      // Curved Studio Stage Backdrop Wall
      const wallGeo = new THREE.CylinderGeometry(6, 6, 8, 32, 1, true, -Math.PI / 2, Math.PI);
      const wallMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(currentBackdrop.bg),
        roughness: 0.8,
        metalness: 0.1,
        side: THREE.BackSide
      });
      const wallMesh = new THREE.Mesh(wallGeo, wallMat);
      wallMesh.position.set(0, 1.8, 0);
      pGroup.add(wallMesh);

      scene.add(pGroup);
    }
  }, [isEngineReady, backdropEnv, pedestalVisible, pedestalColor, pedestalRingColor]);

  // 4. Mesh, Texture & Material Update
  useEffect(() => {
    if (!isEngineReady || !sceneRef.current || !rendererRef.current) return;
    const scene = sceneRef.current;

    // Clean up previous active textures
    activeTexturesRef.current.forEach(t => t.dispose());
    activeTexturesRef.current = [];

    // 4. Generate Front Label Textures with Multi-Map PBR (Diffuse, Roughness, Metalness, Bump)
    const tW = template?.width || 100;
    const tH = template?.height || 100;

    const buildScene = async () => {
      if (!sceneRef.current || !rendererRef.current) return;

      const labelCanvas = createProportionalCanvas(tW, tH, 1024);
      const roughnessCanvas = createProportionalCanvas(tW, tH, 1024);
      const metalnessCanvas = createProportionalCanvas(tW, tH, 1024);
      const bumpCanvas = createProportionalCanvas(tW, tH, 1024);

      await renderTemplateMultiMapToCanvases({
        labelCanvas,
        roughnessCanvas,
        metalnessCanvas,
        bumpCanvas,
        template,
        csvData,
        elementMaterials: threeD.elementMaterials,
        globalFinish: selectedFinish,
        globalEmbossMode: embossMode,
        globalReliefHeight: reliefHeight,
        highlightElementId: highlightActiveElement ? (threeD.selectedElementIdForMaterial || (template?.elements?.[0]?.id ?? null)) : null
      });

      const uvCanvas = document.createElement('canvas');
      uvCanvas.width = 2048; uvCanvas.height = 2048;

      const uvRoughnessCanvas = document.createElement('canvas');
      uvRoughnessCanvas.width = 2048; uvRoughnessCanvas.height = 2048;

      const uvMetalnessCanvas = document.createElement('canvas');
      uvMetalnessCanvas.width = 2048; uvMetalnessCanvas.height = 2048;

      const uvBumpCanvas = document.createElement('canvas');
      uvBumpCanvas.width = 2048; uvBumpCanvas.height = 2048;

      const uvProjParams = {
        selectedModel,
        boxStickerFace,
        templateWidth: tW,
        templateHeight: tH,
        uvScale,
        uvX,
        uvY,
        uvRotation,
        uvRepeatX,
        uvRepeatY,
        wrapAround
      };

      projectCanvasToUvMap({ ...uvProjParams, srcCanvas: labelCanvas, destCanvas: uvCanvas });
      projectCanvasToUvMap({ ...uvProjParams, srcCanvas: roughnessCanvas, destCanvas: uvRoughnessCanvas, clearColor: 'rgb(115,115,115)' });
      projectCanvasToUvMap({ ...uvProjParams, srcCanvas: metalnessCanvas, destCanvas: uvMetalnessCanvas, clearColor: 'rgb(0,0,0)' });
      projectCanvasToUvMap({ ...uvProjParams, srcCanvas: bumpCanvas, destCanvas: uvBumpCanvas, clearColor: 'rgb(128,128,128)' });

      const labelTexture = new THREE.CanvasTexture(uvCanvas);
      labelTexture.colorSpace = THREE.SRGBColorSpace;
      labelTexture.wrapS = THREE.RepeatWrapping;
      labelTexture.wrapT = THREE.RepeatWrapping;

      const roughnessTexture = new THREE.CanvasTexture(uvRoughnessCanvas);
      roughnessTexture.wrapS = THREE.RepeatWrapping;
      roughnessTexture.wrapT = THREE.RepeatWrapping;

      const metalnessTexture = new THREE.CanvasTexture(uvMetalnessCanvas);
      metalnessTexture.wrapS = THREE.RepeatWrapping;
      metalnessTexture.wrapT = THREE.RepeatWrapping;

      const elementBumpTexture = new THREE.CanvasTexture(uvBumpCanvas);
      elementBumpTexture.wrapS = THREE.RepeatWrapping;
      elementBumpTexture.wrapT = THREE.RepeatWrapping;

      activeTexturesRef.current.push(labelTexture, roughnessTexture, metalnessTexture, elementBumpTexture);
    const backCanvas = document.createElement('canvas');
    backCanvas.width = 1024; backCanvas.height = 1024;
    renderBackLabelToCanvas(backCanvas, backLabelTitle, template, csvData);
    const backTexture = new THREE.CanvasTexture(backCanvas);
    backTexture.colorSpace = THREE.SRGBColorSpace;
    activeTexturesRef.current.push(backTexture);

    const neckCanvas = document.createElement('canvas');
    neckCanvas.width = 1024; neckCanvas.height = 256;
    renderNeckLabelToCanvas(neckCanvas, neckLabelText);
    const neckTexture = new THREE.CanvasTexture(neckCanvas);
    neckTexture.colorSpace = THREE.SRGBColorSpace;
    activeTexturesRef.current.push(neckTexture);

    // 4c. Tactile Embossing Bump Map & Condensation Map
    let dropletBumpMap: THREE.CanvasTexture | null = null;
    if (showCondensation) {
      dropletBumpMap = createCondensationDropletsTexture();
      if (dropletBumpMap) activeTexturesRef.current.push(dropletBumpMap);
    }

    // Material Finish Properties
    const finishObj = MATERIAL_FINISHES.find(f => f.id === selectedFinish) || MATERIAL_FINISHES[0];
    const hasCustomElementFinishes = (Object.values(threeD.elementMaterials || {}) as any[]).some(m => m && m.finish && m.finish !== 'default') ||
      (template?.elements || []).some((el: any) => el.materialFinish && el.materialFinish !== 'default');

    const activeLabelBump = (hasCustomElementFinishes || embossMode !== 'none') 
      ? elementBumpTexture 
      : (showCondensation ? dropletBumpMap : null);

    const activeLabelBumpScale = (hasCustomElementFinishes || embossMode !== 'none') 
      ? 0.16 
      : (showCondensation ? 0.12 : 0);

    const dispScale = (hasCustomElementFinishes || embossMode !== 'none') ? 0.035 : 0;
    const dispBias = (hasCustomElementFinishes || embossMode !== 'none') ? -0.017 : 0;

    const isHolographic = selectedFinish === 'holographic' || (finishObj as any).id === 'holographic' || (finishObj as any).finish === 'holographic';
    const isGoldFoil = selectedFinish === 'gold_foil' || (finishObj as any).id === 'gold_foil' || (finishObj as any).finish === 'gold_foil';
    const isMetallic = finishObj.metalness > 0.5 || isGoldFoil || selectedFinish === 'silver_foil' || isHolographic || hasCustomElementFinishes;
    const effEnvIntensity = isMetallic ? (isGoldFoil ? 1.85 : (isHolographic ? 1.6 : 1.4)) : (embossMode === 'spot_uv' ? 0.65 : (selectedFinish === 'gloss' ? 0.4 : 0.22));

    const labelMaterial = new THREE.MeshStandardMaterial({
      map: labelTexture,
      roughnessMap: roughnessTexture,
      metalnessMap: metalnessTexture,
      bumpMap: activeLabelBump,
      bumpScale: activeLabelBumpScale,
      displacementMap: (hasCustomElementFinishes || embossMode !== 'none') ? elementBumpTexture : null,
      displacementScale: dispScale,
      displacementBias: dispBias,
      color: finishObj.color ? new THREE.Color(finishObj.color) : (selectedFinish === 'gold_foil' || selectedFinish === 'silver_foil' ? new THREE.Color(selectiveFinishColor) : new THREE.Color(0xffffff)),
      roughness: isGoldFoil ? 0.08 : (isHolographic ? 0.06 : 1.0),
      metalness: isGoldFoil ? 0.98 : (isHolographic ? 0.98 : 1.0),
      transparent: true,
      opacity: finishObj.opacity || 1.0,
      envMapIntensity: effEnvIntensity
    });

    labelMaterial.onBeforeCompile = (shader) => {
      if (embossMode !== 'none') {
        shader.uniforms.uReliefHeight = { value: reliefHeight };
        shader.uniforms.uEmbossSign = { value: embossMode === 'debossed' ? -1.0 : (embossMode === 'spot_uv' ? 1.5 : 1.0) };
        
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <roughnessmap_fragment>',
          `
          #include <roughnessmap_fragment>
          #ifdef USE_DISPLACEMENTMAP
            vec4 dispTexel = texture2D( displacementMap, vDisplacementMapUv );
            if (abs(dispTexel.r - 0.5) > 0.05) {
              roughnessFactor = clamp(roughnessFactor * 0.72, 0.02, 1.0);
            }
          #endif
          `
        );
      }

      if (isHolographic) {
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <dithering_fragment>',
          `
          #include <dithering_fragment>
          vec3 holoViewDir = normalize(vViewPosition);
          vec3 holoNormal = normalize(vNormal);
          float holoCos = abs(dot(holoViewDir, holoNormal));
          float holoHue = fract(holoCos * 2.5 + dot(holoNormal.xy, vec2(1.0, 1.0)) * 0.35);
          vec3 rainbowHolo = clamp(abs(mod(holoHue * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
          gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * rainbowHolo * 1.9, 0.62);
          `
        );
      }
    };

    const backLabelMaterial = new THREE.MeshStandardMaterial({
      map: backTexture,
      bumpMap: showCondensation ? dropletBumpMap : null,
      bumpScale: showCondensation ? 0.12 : 0,
      roughness: 0.8,
      metalness: 0.0
    });

    const neckLabelMaterial = new THREE.MeshStandardMaterial({
      map: neckTexture,
      bumpMap: showCondensation ? dropletBumpMap : null,
      bumpScale: showCondensation ? 0.12 : 0,
      roughness: 0.2,
      metalness: 0.8,
      color: new THREE.Color(0xf59e0b)
    });

    const cMat = CONTAINER_MATERIALS.find(m => m.id === containerMaterial) || CONTAINER_MATERIALS[0];

    const effRoughness = useCustomMaterial ? containerRoughness : cMat.roughness;
    const effMetalness = useCustomMaterial ? containerMetalness : cMat.metalness;
    const effTransmission = useCustomMaterial ? containerTransmission : (cMat.transmission || 0);
    const effIor = useCustomMaterial ? containerIor : (cMat.ior || 1.5);

    const bodyMaterial = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(cMat.overrideColor || containerColor),
      roughness: showCondensation ? Math.min(effRoughness, 0.2) : effRoughness,
      metalness: effMetalness,
      transmission: effTransmission,
      opacity: cMat.opacity || 1.0,
      transparent: effTransmission > 0 || (cMat.opacity || 1.0) < 1.0,
      ior: effIor,
      thickness: cMat.thickness || 0.1,
      bumpMap: dropletBumpMap,
      bumpScale: showCondensation ? 0.15 : 0,
      clearcoat: showCondensation ? 1.0 : (cMat.id === 'plastic_glossy' ? 0.4 : 0),
      clearcoatRoughness: 0.1,
      envMapIntensity: Math.min(cMat.envMapIntensity || 0.45, 0.65)
    });

    const capMatDef = CONTAINER_MATERIALS.find(m => m.id === capMaterialId) || CONTAINER_MATERIALS[0];
    const capMaterial = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(capColor),
      roughness: capMatDef.roughness,
      metalness: capMatDef.metalness,
      envMapIntensity: Math.min(capMatDef.envMapIntensity || 0.45, 0.65)
    });

    // 5. Construct 3D Packaging Models
    let meshGroup = new THREE.Group();
    const liftVal = (stickerOffsetZ / 1000) * 2.0;

    if (selectedModel === 'can') {
      const canGeo = new THREE.CylinderGeometry(0.9, 0.9, 2.8, 48);
      const canMesh = new THREE.Mesh(canGeo, bodyMaterial);
      canMesh.castShadow = true;
      meshGroup.add(canMesh);
      
      const stickerGeo = new THREE.CylinderGeometry(0.9 + liftVal, 0.9 + liftVal, 2.8, 48);
      const stickerMesh = new THREE.Mesh(stickerGeo, [labelMaterial, new THREE.MeshBasicMaterial({transparent:true,opacity:0}), new THREE.MeshBasicMaterial({transparent:true,opacity:0})]);
      meshGroup.add(stickerMesh);

      if (enableMultiLabel) {
        const backGeo = new THREE.CylinderGeometry(0.9 + liftVal + 0.001, 0.9 + liftVal + 0.001, 1.8, 48, 1, false, Math.PI * 0.7, Math.PI * 0.6);
        const backMesh = new THREE.Mesh(backGeo, backLabelMaterial);
        meshGroup.add(backMesh);
      }

    } else if (selectedModel === 'box') {
      const boxGeo = new THREE.BoxGeometry(2.2, 2.6, 1.6);
      const boxMesh = new THREE.Mesh(boxGeo, bodyMaterial);
      boxMesh.castShadow = true;
      meshGroup.add(boxMesh);
      
      const stickerGeo = new THREE.BoxGeometry(2.2 + liftVal * 2, 2.6 + liftVal * 2, 1.6 + liftVal * 2);
      const emptyMat = new THREE.MeshBasicMaterial({transparent:true, opacity:0});
      
      // Box Geometry Face Index:
      // 0: Right (+X), 1: Left (-X), 2: Top (+Y), 3: Bottom (-Y), 4: Front (+Z), 5: Back (-Z)
      let boxMats: THREE.Material[] = [emptyMat, emptyMat, emptyMat, emptyMat, emptyMat, emptyMat];
      if (boxStickerFace === 'all') {
        boxMats = [labelMaterial, labelMaterial, labelMaterial, labelMaterial, labelMaterial, labelMaterial];
      } else if (boxStickerFace === 'front') {
        boxMats = [emptyMat, emptyMat, emptyMat, emptyMat, labelMaterial, enableMultiLabel ? backLabelMaterial : emptyMat];
      } else if (boxStickerFace === 'back') {
        boxMats = [emptyMat, emptyMat, emptyMat, emptyMat, enableMultiLabel ? labelMaterial : emptyMat, labelMaterial];
      } else if (boxStickerFace === 'left') {
        boxMats = [emptyMat, labelMaterial, emptyMat, emptyMat, emptyMat, emptyMat];
      } else if (boxStickerFace === 'right') {
        boxMats = [labelMaterial, emptyMat, emptyMat, emptyMat, emptyMat, emptyMat];
      } else if (boxStickerFace === 'top') {
        boxMats = [emptyMat, emptyMat, labelMaterial, emptyMat, emptyMat, emptyMat];
      } else if (boxStickerFace === 'bottom') {
        boxMats = [emptyMat, emptyMat, emptyMat, labelMaterial, emptyMat, emptyMat];
      }

      const stickerMesh = new THREE.Mesh(stickerGeo, boxMats);
      meshGroup.add(stickerMesh);

    } else if (selectedModel === 'pouch') {
      const pouchGeo = new THREE.CylinderGeometry(1.1, 1.2, 2.8, 32);
      pouchGeo.scale(1.0, 1.0, 0.4);
      const pouchMesh = new THREE.Mesh(pouchGeo, bodyMaterial);
      pouchMesh.castShadow = true;
      meshGroup.add(pouchMesh);

      const stickerGeo = new THREE.CylinderGeometry(1.1 + liftVal, 1.2 + liftVal, 2.8, 32);
      stickerGeo.scale(1.0, 1.0, 0.4 + liftVal);
      const stickerMesh = new THREE.Mesh(stickerGeo, [labelMaterial, new THREE.MeshBasicMaterial({transparent:true,opacity:0}), new THREE.MeshBasicMaterial({transparent:true,opacity:0})]);
      meshGroup.add(stickerMesh);

    } else if (selectedModel === 'jar') {
      const jarGeo = new THREE.CylinderGeometry(1.2, 1.2, 1.6, 48);
      const jarMesh = new THREE.Mesh(jarGeo, bodyMaterial);
      jarMesh.castShadow = true;
      meshGroup.add(jarMesh);

      const stickerGeo = new THREE.CylinderGeometry(1.2 + liftVal, 1.2 + liftVal, 1.4, 48);
      const stickerMesh = new THREE.Mesh(stickerGeo, [labelMaterial, new THREE.MeshBasicMaterial({transparent:true,opacity:0}), new THREE.MeshBasicMaterial({transparent:true,opacity:0})]);
      meshGroup.add(stickerMesh);

      const capGeo = new THREE.CylinderGeometry(1.22, 1.22, 0.4, 48);
      capGeo.translate(0, 1.0, 0);
      const capMesh = new THREE.Mesh(capGeo, capMaterial);
      meshGroup.add(capMesh);

    } else if (selectedModel === 'cup') {
      const cupGeo = new THREE.CylinderGeometry(1.1, 0.8, 2.8, 48);
      const cupMesh = new THREE.Mesh(cupGeo, bodyMaterial);
      cupMesh.castShadow = true;
      meshGroup.add(cupMesh);

      const stickerGeo = new THREE.CylinderGeometry(1.1 + liftVal, 0.8 + liftVal, 2.4, 48);
      const stickerMesh = new THREE.Mesh(stickerGeo, [labelMaterial, new THREE.MeshBasicMaterial({transparent:true,opacity:0}), new THREE.MeshBasicMaterial({transparent:true,opacity:0})]);
      meshGroup.add(stickerMesh);

      const lidGeo = new THREE.CylinderGeometry(1.15, 1.15, 0.3, 48);
      lidGeo.translate(0, 1.5, 0);
      const lidMesh = new THREE.Mesh(lidGeo, capMaterial);
      meshGroup.add(lidMesh);

    } else {
      // Default: Glass Beverage Bottle
      const bodyGeo = new THREE.CylinderGeometry(0.88, 0.88, 2.2, 48);
      const bodyMesh = new THREE.Mesh(bodyGeo, bodyMaterial);
      bodyMesh.position.y = -0.2;
      bodyMesh.castShadow = true;
      meshGroup.add(bodyMesh);
      
      const stickerGeo = new THREE.CylinderGeometry(0.88 + liftVal, 0.88 + liftVal, 2.2, 48);
      const stickerMesh = new THREE.Mesh(stickerGeo, [labelMaterial, new THREE.MeshBasicMaterial({transparent:true,opacity:0}), new THREE.MeshBasicMaterial({transparent:true,opacity:0})]);
      stickerMesh.position.y = -0.2;
      meshGroup.add(stickerMesh);

      if (enableMultiLabel) {
        // Back Ingredients Label Mesh
        const backGeo = new THREE.CylinderGeometry(0.88 + liftVal + 0.001, 0.88 + liftVal + 0.001, 1.6, 48, 1, false, Math.PI * 0.7, Math.PI * 0.6);
        const backMesh = new THREE.Mesh(backGeo, backLabelMaterial);
        backMesh.position.y = -0.2;
        meshGroup.add(backMesh);

        // Neck Seal Ring Label Mesh
        const neckGeo = new THREE.CylinderGeometry(0.42 + liftVal, 0.52 + liftVal, 0.4, 48);
        neckGeo.translate(0, 1.35, 0);
        const neckMesh = new THREE.Mesh(neckGeo, neckLabelMaterial);
        meshGroup.add(neckMesh);
      }

      const shoulderGeo = new THREE.CylinderGeometry(0.35, 0.88, 1.0, 48);
      shoulderGeo.translate(0, 1.2, 0);
      const shoulderMesh = new THREE.Mesh(shoulderGeo, bodyMaterial);
      meshGroup.add(shoulderMesh);

      const capGeo = new THREE.CylinderGeometry(0.37, 0.37, 0.4, 32);
      capGeo.translate(0, 1.85, 0);
      const capMesh = new THREE.Mesh(capGeo, capMaterial);
      meshGroup.add(capMesh);
    }

    // 5b. Attach packaging model to scene
    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          const m = child as THREE.Mesh;
          if (m.geometry) m.geometry.dispose();
          if (Array.isArray(m.material)) m.material.forEach(mat => mat.dispose());
          else if (m.material) m.material.dispose();
        }
      });
      meshRef.current = null;
    }

      meshRef.current = meshGroup;
      meshGroup.rotation.x = meshRotationRef.current.x;
      meshGroup.rotation.y = meshRotationRef.current.y;
      scene.add(meshGroup);
    };

    buildScene();
  }, [
    isEngineReady, template, csvData, selectedModel, selectedFinish, containerColor, containerMaterial, 
    capColor, capMaterialId, boxStickerFace, stickerOffsetZ, uvScale, uvX, uvY, uvRotation, uvRepeatX, uvRepeatY, wrapAround, selectiveFinishColor,
    showCondensation, embossMode, reliefHeight, tactileTargetArea, edgeSoftness, enableMultiLabel, 
    backLabelTitle, neckLabelText,
    useCustomMaterial, containerRoughness, containerMetalness, containerTransmission, containerIor,
    threeD.elementMaterials, threeD.selectedElementIdForMaterial, highlightActiveElement
  ]);

  // Update camera position when zoomLevel or cameraAngle changes
  useEffect(() => {
    if (!cameraRef.current) return;
    const selectedCamAngle = CAMERA_ANGLES.find(c => c.id === cameraAngle) || CAMERA_ANGLES[0];
    const dirVec = new THREE.Vector3(selectedCamAngle.dir[0], selectedCamAngle.dir[1], selectedCamAngle.dir[2]).normalize();
    const currentDist = (selectedCamAngle.baseDist || 4.8) / zoomLevel;
    
    cameraRef.current.position.set(dirVec.x * currentDist, dirVec.y * currentDist, dirVec.z * currentDist);
    cameraRef.current.lookAt(0, 0, 0);
  }, [zoomLevel, cameraAngle]);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 3.0));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.4));
  const handleResetView = () => {
    setZoomLevel(1.0);
    setCameraAngle('isometric');
    setUvX(50);
    setUvY(50);
    setUvScale(1.0);
    setStickerOffsetZ(1.0);
    setAutoRotate(true);
    if (meshRef.current) {
      meshRef.current.rotation.set(0, 0, 0);
    }
  };

  const handleBoxFaceChange = (face: 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom' | 'all') => {
    setBoxStickerFace(face);
    if (!meshRef.current) return;
    
    switch (face) {
      case 'front':
      case 'all':
        meshRef.current.rotation.set(0, 0, 0);
        meshRotationRef.current = { x: 0, y: 0 };
        break;
      case 'back':
        meshRef.current.rotation.set(0, Math.PI, 0);
        meshRotationRef.current = { x: 0, y: Math.PI };
        break;
      case 'left':
        meshRef.current.rotation.set(0, Math.PI / 2, 0);
        meshRotationRef.current = { x: 0, y: Math.PI / 2 };
        break;
      case 'right':
        meshRef.current.rotation.set(0, -Math.PI / 2, 0);
        meshRotationRef.current = { x: 0, y: -Math.PI / 2 };
        break;
      case 'top':
        meshRef.current.rotation.set(Math.PI / 2, 0, 0);
        meshRotationRef.current = { x: Math.PI / 2, y: 0 };
        break;
      case 'bottom':
        meshRef.current.rotation.set(-Math.PI / 2, 0, 0);
        meshRotationRef.current = { x: -Math.PI / 2, y: 0 };
        break;
    }
  };

  const handleLightingPresetSelect = (presetId: string) => {
    setLightingPreset(presetId);
    const p = LIGHTING_PRESETS.find(x => x.id === presetId);
    if (!p) return;

    if (p.ambient !== undefined) setAmbientIntensity(p.ambient);
    if (p.mainColor !== undefined) setLightColor('#' + p.mainColor.toString(16).padStart(6, '0'));
    if (p.rimColor !== undefined) setRimLightColor('#' + p.rimColor.toString(16).padStart(6, '0'));

    if (presetId === 'studio_dark') {
      setLightIntensity(1.8);
      setRimLightIntensity(0.8);
      setToneMappingExposure(0.95);
      handleBackdropEnvSelect('studio_dark');
    } else if (presetId === 'luxury_white') {
      setLightIntensity(2.2);
      setRimLightIntensity(0.7);
      setToneMappingExposure(0.90);
      handleBackdropEnvSelect('marble_counter');
    } else if (presetId === 'warm_sun') {
      setLightIntensity(2.0);
      setRimLightIntensity(1.0);
      setToneMappingExposure(0.92);
      handleBackdropEnvSelect('rustic_wood');
    } else if (presetId === 'neon_stage') {
      setLightIntensity(1.9);
      setRimLightIntensity(1.1);
      setToneMappingExposure(0.95);
      handleBackdropEnvSelect('cyber_stage');
    }
  };

  const handleBackdropEnvSelect = (envId: string) => {
    setBackdropEnv(envId);
    const env = BACKDROP_ENVIRONMENTS.find(e => e.id === envId);
    if (!env) return;

    const floorHex = '#' + env.floorColor.toString(16).padStart(6, '0');
    const ringHex = '#' + env.ringColor.toString(16).padStart(6, '0');
    setPedestalColor(floorHex);
    setPedestalRingColor(ringHex);

    if (envId === 'studio_dark') {
      setToneMappingExposure(1.0);
      setLightColor('#ffffff');
      setRimLightColor('#a855f7');
      setAmbientIntensity(0.6);
      setRimLightIntensity(2.0);
    } else if (envId === 'retail_shelf') {
      setToneMappingExposure(1.1);
      setLightColor('#f8fafc');
      setRimLightColor('#10b981');
      setAmbientIntensity(0.8);
      setRimLightIntensity(1.8);
    } else if (envId === 'marble_counter') {
      setToneMappingExposure(1.2);
      setLightColor('#ffffff');
      setRimLightColor('#d97706');
      setAmbientIntensity(0.9);
      setRimLightIntensity(1.5);
    } else if (envId === 'rustic_wood') {
      setToneMappingExposure(1.05);
      setLightColor('#fbbf24');
      setRimLightColor('#f59e0b');
      setAmbientIntensity(0.7);
      setRimLightIntensity(2.2);
    } else if (envId === 'cyber_stage') {
      setToneMappingExposure(1.3);
      setLightColor('#0284c7');
      setRimLightColor('#ec4899');
      setAmbientIntensity(0.5);
      setRimLightIntensity(3.5);
    }
  };

  // Direct PNG Download
  const handleDownloadProofPng = () => {
    try {
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        const dataUrl = rendererRef.current.domElement.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `3D-Packaging-Proof-${template?.name || 'Sticker'}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('3D High-Res Proof PNG Downloaded!');
      } else {
        toast.error('3D Scene renderer is not ready yet.');
      }
    } catch (err: any) {
      console.error('Download PNG failed:', err);
      toast.error('Export failed: ' + (err?.message || 'Could not export canvas'));
    }
  };

  // 360 Spin Record Handler
  const handleStart360Record = async () => {
    try {
      if (!meshRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current) {
        toast.error('3D Model is not ready yet');
        return;
      }
      setIsRecording360(true);
      setAutoRotate(false);
      toast.info('Capturing 360° Spin Frame Snapshots...');

      const snapshots: string[] = [];
      const totalFrames = 24;
      const startY = meshRef.current.rotation.y;

      for (let i = 0; i < totalFrames; i++) {
        meshRef.current.rotation.y = startY + (i / totalFrames) * Math.PI * 2;
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        const dataUrl = rendererRef.current.domElement.toDataURL('image/png');
        snapshots.push(dataUrl);
        await new Promise(r => setTimeout(r, 40));
      }

      setSpinSnapshots(snapshots);
      setIsRecording360(false);
      setShowSpinGallery(true);
      toast.success('360° Spin Animation Captured!');
    } catch (err: any) {
      setIsRecording360(false);
      console.error('360 Spin recording failed:', err);
      toast.error('Recording failed: ' + (err?.message || 'Error capturing frames'));
    }
  };

  // Generate Official PDF Proof Certificate Modal with QR Code
  const handleGeneratePdfProof = async () => {
    try {
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        const renderSnap = rendererRef.current.domElement.toDataURL('image/png');
        setProofSnapshotUrl(renderSnap);

        try {
          const qrData = await QRCode.toDataURL(window.location.href);
          setProofQrCodeUrl(qrData);
        } catch (qrErr) {
          console.warn('QR code generation fallback:', qrErr);
          setProofQrCodeUrl('');
        }

        setShowPdfProofModal(true);
      } else {
        toast.error('3D Scene renderer is not ready yet.');
      }
    } catch (e: any) {
      console.error('PDF Proof generation error:', e);
      toast.error('Failed to generate PDF proof sheet: ' + (e?.message || ''));
    }
  };

  if (!isOpen) return null;

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="3D Packaging & Label Application Studio" maxWidth="max-w-[98vw]">
      <div className="flex flex-col w-full h-[90vh] overflow-hidden dark-studio-scope bg-zinc-950 text-zinc-100 font-sans">
      
      {/* Modal Studio Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-zinc-950 shrink-0 text-zinc-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 text-white shadow-md flex items-center justify-center shrink-0">
            <Box className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-zinc-100 flex items-center gap-2">
              3D Packaging &amp; Label Application Studio
              <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-mono px-2 py-0.5 rounded-md border border-emerald-500/40 font-bold">
                Studio Pro
              </span>
            </h3>
            <p className="text-[11px] text-zinc-400">UV Offset Mapping, 360° Turntable, PBR Finishes &amp; Print Proof Export</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadProofPng}
            className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
          >
            <Download className="w-3.5 h-3.5 text-blue-400" />
            <span>Download PNG</span>
          </button>

          <button
            onClick={handleStart360Record}
            disabled={isRecording360}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs disabled:opacity-50"
          >
            <Film className="w-3.5 h-3.5" />
            <span>{isRecording360 ? 'Recording...' : 'Record 360° Spin'}</span>
          </button>

          <button
            onClick={handleGeneratePdfProof}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-xs"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Export PDF Proof</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-100 p-2 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex flex-1 overflow-hidden relative">
        
        {/* Left: 3D Viewport */}
        <div className="flex-1 flex flex-col relative bg-zinc-950 overflow-hidden">
          
          {/* Header Proof Metadata Tag */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800 bg-zinc-900/95 px-4 py-2 text-xs shrink-0 z-10">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${
                approvalStatus === 'APPROVED' ? 'bg-emerald-400 shadow-xs shadow-emerald-400/50' :
                approvalStatus === 'REVISION' ? 'bg-amber-400 shadow-xs shadow-amber-400/50' :
                'bg-blue-400 animate-pulse'
              }`} />
              <span className="font-bold text-zinc-100 uppercase tracking-wider font-mono text-xs">
                OFFICIAL 3D CLIENT PROOF #{template.id ? template.id.substring(0, 8).toUpperCase() : 'PRF-2026'}
              </span>
            </div>
            <div className="flex items-center gap-3 font-mono text-[11px] text-zinc-400">
              <span>Client: <strong className="text-zinc-100">{clientName || 'Demo Customer'}</strong></span>
              <span className={`px-2 py-0.5 rounded font-bold border ${
                approvalStatus === 'APPROVED' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                approvalStatus === 'REVISION' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                'bg-blue-500/20 text-blue-300 border-blue-500/40'
              }`}>
                {approvalStatus}
              </span>
            </div>
          </div>
          
          {/* 3D Viewport Canvas Container */}
          <div className="relative w-full flex-1 min-h-[350px] overflow-hidden">
            <ThreeDView 
              onCanvasMount={setMountRef} 
              className="absolute inset-0 w-full h-full" 
            />
            
            {/* WebGL Error / Context Lost Fallback Overlay */}
            {(threeD.engineStatus === 'error' || threeD.engineStatus === 'context_lost') && (
              <div className="absolute inset-0 z-30 bg-zinc-950/90 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center">
                <div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400 mb-4 shadow-lg">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold text-base mb-1 font-sans">
                  {threeD.engineStatus === 'context_lost' ? 'WebGL GPU Context Lost' : '3D Engine Initialization Failed'}
                </h4>
                <p className="text-zinc-400 text-xs max-w-md mb-5 leading-relaxed">
                  {threeD.errorMessage || 'Unable to initialize WebGL graphics pipeline. Please verify browser hardware acceleration or restart the 3D renderer.'}
                </p>
                <button
                  type="button"
                  onClick={() => threeD.retryEngineInit()}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-95 text-white text-xs font-semibold flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Re-initialize 3D Engine</span>
                </button>
              </div>
            )}

            {/* WebGL Initializing Spinner */}
            {threeD.engineStatus === 'initializing' && !isEngineReady && (
              <div className="absolute inset-0 z-20 bg-zinc-950/40 backdrop-blur-sm flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-zinc-900/90 border border-zinc-800 text-xs font-mono text-zinc-300 shadow-xl">
                  <RefreshCw className="w-3.5 h-3.5 text-blue-400 animate-spin" />
                  <span>Booting 3D Studio WebGL...</span>
                </div>
              </div>
            )}
            
            {/* Viewport Rotate Indicator Badge */}
            <div className="absolute top-4 left-4 bg-zinc-900/90 backdrop-blur-md px-3 py-1.5 rounded-lg border border-zinc-700/80 text-[11px] font-mono text-zinc-200 flex items-center gap-2 shadow-xl">
              <RotateCw className="w-3.5 h-3.5 text-blue-400" />
              <span>Drag to Rotate • Scroll to Zoom</span>
            </div>
            
            {/* Interactive Zoom Controls Toolbar */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-zinc-900/90 backdrop-blur-md p-1 rounded-xl border border-zinc-700/80 shadow-2xl z-20">
              <button
                type="button"
                onClick={handleZoomOut}
                className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 transition-colors cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4 text-zinc-300" />
              </button>
              <div className="px-2.5 font-mono text-xs font-bold text-zinc-100 min-w-[50px] text-center">
                {Math.round(zoomLevel * 100)}%
              </div>
              <button
                type="button"
                onClick={handleZoomIn}
                className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 transition-colors cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4 text-zinc-300" />
              </button>
              <div className="w-px h-4 bg-zinc-700 mx-0.5" />
              <button
                type="button"
                onClick={handleResetView}
                className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs font-mono font-semibold text-blue-400 hover:text-blue-300 transition-colors cursor-pointer flex items-center gap-1.5"
                title="Center Model & Reset View"
              >
                <Focus className="w-3.5 h-3.5 text-blue-400" />
                <span>Center</span>
              </button>
            </div>
            
            {/* Ambient Toggle & Auto Orbit / Turntable Speed Control */}
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-zinc-900/90 backdrop-blur-md p-1.5 rounded-xl border border-zinc-700/80 shadow-2xl z-20">
              <button
                type="button"
                onClick={() => setAmbientLightEnabled(!ambientLightEnabled)}
                className={`px-2.5 py-1 rounded-lg border text-[11px] font-mono font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  ambientLightEnabled 
                    ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300 shadow-sm' 
                    : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200'
                }`}
                title="Toggle Ambient Light Fill"
              >
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                <span>Ambient: {ambientLightEnabled ? 'ON' : 'OFF'}</span>
              </button>

              <button
                type="button"
                onClick={() => setAutoRotate(prev => !prev)}
                className={`px-2.5 py-1 rounded-lg border text-[11px] font-mono font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  autoRotate 
                    ? 'bg-blue-600 border-blue-500 text-white shadow-sm' 
                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:text-white'
                }`}
              >
                <Sun className="w-3.5 h-3.5 text-amber-400" />
                <span>{autoRotate ? 'Turntable ON' : 'Turntable OFF'}</span>
              </button>
              {autoRotate && (
                <div className="flex items-center gap-1 px-2 text-[10px] font-mono text-zinc-300 border-l border-zinc-700">
                  <span>Speed:</span>
                  <input
                    type="range"
                    min="0.2"
                    max="3.0"
                    step="0.2"
                    value={turntableSpeed}
                    onChange={(e) => setTurntableSpeed(parseFloat(e.target.value))}
                    className="w-16 accent-blue-500 cursor-pointer"
                  />
                  <span className="w-6 font-bold">{turntableSpeed.toFixed(1)}x</span>
                </div>
              )}
            </div>

            {/* Camera Angles Selector */}
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 bg-zinc-900/90 backdrop-blur-md p-2 rounded-xl border border-zinc-700/80 text-xs shadow-2xl z-20">
              <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar">
                <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider px-1 whitespace-nowrap">Camera Angle:</span>
                {CAMERA_ANGLES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCameraAngle(c.id); setAutoRotate(false); }}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                      cameraAngle === c.id
                        ? 'bg-blue-600 text-white border border-blue-500 shadow-sm'
                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-zinc-700/80'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Client Sign-Off Footer Box */}
          <div className="bg-zinc-900 px-5 py-2.5 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-3 text-xs shrink-0 z-10">
            <div className="flex items-center gap-2 text-zinc-300 w-full md:w-auto flex-1">
              <FileCheck2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="font-semibold text-zinc-200">Notes:</span>
              <input 
                type="text"
                value={clientNotes}
                onChange={(e) => setClientNotes(e.target.value)}
                className="bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1 rounded outline-none focus:border-blue-500 font-mono w-full max-w-md"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-zinc-400 font-bold uppercase">Sign-off Status:</span>
              <select
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value as any)}
                className="bg-zinc-950 border border-zinc-700 text-zinc-100 text-xs px-2.5 py-1 rounded outline-none focus:border-emerald-500 font-bold cursor-pointer"
              >
                <option value="AWAITING">Awaiting Sign-off</option>
                <option value="APPROVED">APPROVED FOR PRINT</option>
                <option value="REVISION">Revision Requested</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Right: Sidebar Controls */}
        <div className="w-[390px] bg-zinc-900 border-l border-zinc-800 flex flex-col shrink-0 relative shadow-2xl z-20">
          
          {/* Quick Category Navigation Tabs */}
          <div className="flex items-center gap-1 p-2 bg-zinc-950 border-b border-zinc-800 overflow-x-auto text-[10px] shrink-0 custom-scrollbar">
            {[
              { id: 'all', label: 'All Controls', icon: LayoutGrid },
              { id: 'elementMaterials', label: 'Element Finishes', icon: Sparkles },
              { id: 'substrate', label: 'Material', icon: Box },
              { id: 'uv', label: 'UV Mapping', icon: Sliders },
              { id: 'light', label: 'Light Setup', icon: Sun },
              { id: 'environment', label: 'Stage & Env', icon: Sparkle },
              { id: 'finish', label: 'Global Finish', icon: Palette },
              { id: 'multilabel', label: 'Multi-Label', icon: Layers }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as any);
                    if (tab.id !== 'all') {
                      setOpenSections(prev => ({ ...prev, [tab.id]: true }));
                    }
                  }}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-bold border transition-all cursor-pointer whitespace-nowrap shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-amber-500/20 text-amber-300 border-amber-500 shadow-sm'
                      : 'bg-zinc-900/80 text-zinc-400 border-zinc-800 hover:text-zinc-200'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto p-3.5 space-y-3.5 custom-scrollbar">
            
            {/* Expand / Collapse All Header Bar */}
            <div className="flex items-center justify-between pb-1 border-b border-zinc-800/80">
              <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-indigo-400" />
                Collapsible Control Panels
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={expandAllSections}
                  className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800/90 hover:bg-zinc-700 text-[10px] font-medium text-indigo-300 border border-zinc-700/60 transition-colors cursor-pointer"
                  title="Expand all panels"
                >
                  <ChevronsDown className="w-3 h-3 text-indigo-400" />
                  <span>Expand All</span>
                </button>
                <button
                  type="button"
                  onClick={collapseAllSections}
                  className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800/90 hover:bg-zinc-700 text-[10px] font-medium text-zinc-400 border border-zinc-700/60 transition-colors cursor-pointer"
                  title="Collapse all panels"
                >
                  <ChevronsUp className="w-3 h-3 text-zinc-400" />
                  <span>Collapse All</span>
                </button>
              </div>
            </div>

            {/* Section: Particular Element Finishes & Materials (Gold, Silver, Gloss, Emboss, Deboss) */}
            {(activeTab === 'all' || activeTab === 'elementMaterials') && (
            <div className="rounded-xl border border-amber-500/40 bg-zinc-950/95 overflow-hidden transition-all shadow-md">
              <button
                type="button"
                onClick={() => toggleSection('elementMaterials')}
                className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-amber-950/40 via-zinc-900 to-zinc-950 hover:bg-zinc-900/80 transition-colors text-left cursor-pointer border-b border-zinc-800/80"
              >
                <div className="flex items-center gap-2">
                  <div className="p-1 rounded-lg bg-amber-500/20 text-amber-300">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-amber-200 block">Particular Element Materials &amp; Finishes</span>
                    <span className="text-[10px] text-zinc-400">Gold/Silver Foil, Spot Gloss, 3D Emboss &amp; Deboss</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/40">
                    {Object.keys(threeD.elementMaterials || {}).length > 0 ? `${Object.keys(threeD.elementMaterials).length} Applied` : 'Ready'}
                  </span>
                  {openSections.elementMaterials ? <ChevronDown className="w-4 h-4 text-amber-400" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                </div>
              </button>

              {openSections.elementMaterials && (
                <div className="p-3 bg-zinc-950/90">
                  <ElementMaterialSetupPanel
                    elements={template?.elements || []}
                    onUpdateElementInTemplate={handleUpdateElementInTemplate}
                    highlightActiveElement={highlightActiveElement}
                    setHighlightActiveElement={setHighlightActiveElement}
                  />
                </div>
              )}
            </div>
            )}

            {/* Section 1: Packaging Substrate & Custom Material */}
            {(activeTab === 'all' || activeTab === 'substrate') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('substrate')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('substrate'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-400 group-hover:scale-105 transition-transform">
                    <Box className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      1. Substrate &amp; Custom Surface Material
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">Model, Glass/Can/Box, PBR surface overrides</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.substrate ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.substrate ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.substrate ? (
                    <ChevronDown className="w-4 h-4 text-indigo-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.substrate && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                <div className="grid grid-cols-2 gap-2">
                  {OBJECT_MODELS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        setSelectedModel(m.id);
                        setCapColor(m.defaultCap);
                        setContainerMaterial(m.defaultBodyMat || 'plastic_matte');
                        setCapMaterialId(m.defaultCapMat || 'plastic_matte');
                      }}
                      className={`flex items-center gap-2 px-2.5 py-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                        selectedModel === m.id
                          ? 'bg-indigo-600/20 border-indigo-500 text-white shadow-md'
                          : 'bg-zinc-950/60 border-zinc-800/80 text-zinc-400 hover:text-zinc-200'
                      }`}
                    >
                      <span className="text-lg">{m.icon}</span>
                      <span className="text-xs font-bold leading-tight">{m.name}</span>
                    </button>
                  ))}
                </div>

                {/* Material Dropdowns & Base Colors */}
                <div className="space-y-3 bg-zinc-900/60 p-3 rounded-xl border border-zinc-800">
                  <div>
                    <label className="text-[11px] font-semibold text-zinc-400 mb-1 block">Container Material Preset:</label>
                    <select
                      value={containerMaterial}
                      onChange={(e) => setContainerMaterial(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 text-white text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-indigo-500 font-medium"
                    >
                      {CONTAINER_MATERIALS.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Material Parameters Toggle */}
                  <div className="pt-2 border-t border-zinc-800/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-indigo-300">Advanced PBR Surface Overrides</span>
                      <input
                        type="checkbox"
                        checked={useCustomMaterial}
                        onChange={(e) => setUseCustomMaterial(e.target.checked)}
                        className="w-4 h-4 accent-indigo-500 cursor-pointer"
                      />
                    </div>

                    {useCustomMaterial && (
                      <div className="space-y-2 pt-1 font-mono text-[10px]">
                        <div className="space-y-1">
                          <div className="flex justify-between text-zinc-400">
                            <span>Roughness:</span>
                            <span className="text-indigo-300 font-bold">{containerRoughness.toFixed(2)}</span>
                          </div>
                          <input
                            type="range" min="0" max="1" step="0.05"
                            value={containerRoughness}
                            onChange={(e) => setContainerRoughness(parseFloat(e.target.value))}
                            className="w-full accent-indigo-500 cursor-pointer"
                          />
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-zinc-400">
                            <span>Metalness:</span>
                            <span className="text-indigo-300 font-bold">{containerMetalness.toFixed(2)}</span>
                          </div>
                          <input
                            type="range" min="0" max="1" step="0.05"
                            value={containerMetalness}
                            onChange={(e) => setContainerMetalness(parseFloat(e.target.value))}
                            className="w-full accent-indigo-500 cursor-pointer"
                          />
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-zinc-400">
                            <span>Glass Transmission:</span>
                            <span className="text-indigo-300 font-bold">{containerTransmission.toFixed(2)}</span>
                          </div>
                          <input
                            type="range" min="0" max="1" step="0.05"
                            value={containerTransmission}
                            onChange={(e) => setContainerTransmission(parseFloat(e.target.value))}
                            className="w-full accent-indigo-500 cursor-pointer"
                          />
                        </div>

                        <div className="space-y-1">
                          <div className="flex justify-between text-zinc-400">
                            <span>Index of Refraction (IOR):</span>
                            <span className="text-indigo-300 font-bold">{containerIor.toFixed(2)}</span>
                          </div>
                          <input
                            type="range" min="1.0" max="2.3" step="0.05"
                            value={containerIor}
                            onChange={(e) => setContainerIor(parseFloat(e.target.value))}
                            className="w-full accent-indigo-500 cursor-pointer"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-zinc-400 mb-1 flex items-center justify-between">
                      <span>Container Base Color:</span>
                      <span className="font-mono text-zinc-200">{containerColor}</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={containerColor}
                        onChange={(e) => setContainerColor(e.target.value)}
                        className="w-7 h-7 rounded bg-transparent border border-zinc-700 cursor-pointer"
                      />
                      <div className="flex items-center gap-1 overflow-x-auto">
                        {COLOR_SWATCHES.map(c => (
                          <button
                            key={c}
                            onClick={() => setContainerColor(c)}
                            className={`w-4 h-4 rounded-full border border-zinc-600 transition-transform ${containerColor === c ? 'scale-125 border-white ring-2 ring-indigo-500' : 'hover:scale-110'}`}
                            style={{ backgroundColor: c }}
                          ></button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-zinc-800/80 grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] font-semibold text-zinc-400 mb-1 block">Cap Material:</label>
                      <select
                        value={capMaterialId}
                        onChange={(e) => setCapMaterialId(e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 text-white text-xs px-2 py-1 rounded outline-none font-medium"
                      >
                        {CONTAINER_MATERIALS.map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-zinc-400 mb-1 block">Cap Color:</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={capColor}
                          onChange={(e) => setCapColor(e.target.value)}
                          className="w-6 h-6 rounded bg-transparent border border-zinc-700 cursor-pointer"
                        />
                        <span className="text-[10px] font-mono text-zinc-300">{capColor}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
            )}

            {/* Section 2: Sticker Placement, Offsets & UV Mapping */}
            {(activeTab === 'all' || activeTab === 'uv') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('uv')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('uv'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 group-hover:scale-105 transition-transform">
                    <Sliders className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      2. Sticker Placement &amp; UV Mapping
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">Scale, Offset X/Y, Rotation, Repeat grid</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => { 
                      e.stopPropagation();
                      setUvX(50); setUvY(50); setUvScale(1.0); 
                      setUvRotation(0); setUvRepeatX(1); setUvRepeatY(1);
                      setWrapAround(true); 
                      setStickerOffsetZ(1.0);
                    }}
                    className="text-[10px] text-emerald-400 hover:text-emerald-300 font-mono font-bold cursor-pointer mr-1"
                  >
                    Reset UV
                  </button>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.uv ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.uv ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.uv ? (
                    <ChevronDown className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.uv && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                {/* Box Face Selection */}
                {selectedModel === 'box' && (
                  <div className="space-y-1.5 bg-zinc-900/80 p-2.5 rounded-lg border border-zinc-800">
                    <label className="text-[11px] font-bold text-amber-300 flex items-center gap-1.5">
                      <LayoutGrid className="w-3.5 h-3.5" />
                      <span>Select Box Sticker Face:</span>
                    </label>
                    <div className="grid grid-cols-4 gap-1 text-[10px]">
                      {[
                        { id: 'front', label: 'Front' },
                        { id: 'back', label: 'Back' },
                        { id: 'left', label: 'Left' },
                        { id: 'right', label: 'Right' },
                        { id: 'top', label: 'Top' },
                        { id: 'bottom', label: 'Bottom' },
                        { id: 'all', label: 'All Faces' }
                      ].map(f => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => handleBoxFaceChange(f.id as any)}
                          className={`py-1 rounded font-bold border transition-all cursor-pointer ${
                            boxStickerFace === f.id
                              ? 'bg-amber-500 text-zinc-950 border-amber-400'
                              : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sticker Scale Slider */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                    <span className="flex items-center gap-1"><Scaling className="w-3 h-3 text-emerald-400" /> Sticker Scale / Zoom:</span>
                    <span className="text-emerald-300 font-bold">{uvScale.toFixed(2)}x</span>
                  </div>
                  <input
                    type="range"
                    min="0.2"
                    max="3.0"
                    step="0.05"
                    value={uvScale}
                    onChange={(e) => setUvScale(parseFloat(e.target.value))}
                    className="w-full accent-emerald-500 cursor-pointer"
                  />
                </div>

                {/* Horizontal Position / Offset X */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                    <span className="flex items-center gap-1"><MoveHorizontal className="w-3 h-3 text-emerald-400" /> Offset X (Horizontal):</span>
                    <span className="text-emerald-300 font-bold">{uvX}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={uvX}
                    onChange={(e) => setUvX(parseInt(e.target.value))}
                    className="w-full accent-emerald-500 cursor-pointer"
                  />
                </div>

                {/* Vertical Position / Offset Y */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                    <span className="flex items-center gap-1"><MoveVertical className="w-3 h-3 text-emerald-400" /> Offset Y (Vertical):</span>
                    <span className="text-emerald-300 font-bold">{uvY}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={uvY}
                    onChange={(e) => setUvY(parseInt(e.target.value))}
                    className="w-full accent-emerald-500 cursor-pointer"
                  />
                </div>

                {/* Z-Axis Lift Offset (Physical Thickness) */}
                <div className="space-y-1 bg-zinc-900/60 p-2 rounded-lg border border-zinc-800/80">
                  <div className="flex justify-between text-[11px] text-zinc-300 font-mono">
                    <span className="flex items-center gap-1.5 font-bold text-amber-300">
                      <Layers className="w-3.5 h-3.5 text-amber-400" /> Offset Z (3D Thickness Lift):
                    </span>
                    <span className="text-amber-300 font-bold">{stickerOffsetZ.toFixed(1)} mm</span>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="10.0"
                    step="0.1"
                    value={stickerOffsetZ}
                    onChange={(e) => setStickerOffsetZ(parseFloat(e.target.value))}
                    className="w-full accent-amber-500 cursor-pointer"
                  />
                  <p className="text-[9.5px] text-zinc-400 font-medium">
                    Adjusts physical Z-axis separation lift from base container surface (simulates vinyl/emblem thickness).
                  </p>
                </div>

                {/* Sticker Rotation */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                    <span className="flex items-center gap-1"><RotateCw className="w-3 h-3 text-emerald-400" /> Sticker Angle:</span>
                    <span className="text-emerald-300 font-bold">{uvRotation}°</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="5"
                    value={uvRotation}
                    onChange={(e) => setUvRotation(parseInt(e.target.value))}
                    className="w-full accent-emerald-500 cursor-pointer"
                  />
                </div>

                {/* Pattern Repeat Grid */}
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-zinc-800/80 text-[10px]">
                  <div className="space-y-1">
                    <div className="flex justify-between text-zinc-400 font-mono">
                      <span>Repeat X:</span>
                      <span className="text-emerald-300 font-bold">{uvRepeatX}</span>
                    </div>
                    <input
                      type="range" min="1" max="5" step="1"
                      value={uvRepeatX}
                      onChange={(e) => setUvRepeatX(parseInt(e.target.value))}
                      className="w-full accent-emerald-500 cursor-pointer"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-zinc-400 font-mono">
                      <span>Repeat Y:</span>
                      <span className="text-emerald-300 font-bold">{uvRepeatY}</span>
                    </div>
                    <input
                      type="range" min="1" max="5" step="1"
                      value={uvRepeatY}
                      onChange={(e) => setUvRepeatY(parseInt(e.target.value))}
                      className="w-full accent-emerald-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Cylindrical Wrap-Around Toggle */}
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                  <span className="text-[11px] font-semibold text-zinc-300 flex items-center gap-1.5">
                    <RotateCw className="w-3.5 h-3.5 text-emerald-400" />
                    360° Cylindrical Wrap Around
                  </span>
                  <input
                    type="checkbox"
                    checked={wrapAround}
                    onChange={(e) => setWrapAround(e.target.checked)}
                    className="w-4 h-4 accent-emerald-500 cursor-pointer"
                  />
                </div>
              </div>
              )}
            </div>
            )}

            {/* Section 3: Studio Light Setup */}
            {(activeTab === 'all' || activeTab === 'light') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('light')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('light'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 group-hover:scale-105 transition-transform">
                    <Sun className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      3. Studio Light Setup &amp; Shadows
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">Keylight, angles, spotlight, rim backlight</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.light ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.light ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.light ? (
                    <ChevronDown className="w-4 h-4 text-amber-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.light && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                {/* Lighting Presets Selector */}
                <div>
                  <label className="text-[11px] font-semibold text-zinc-400 mb-1.5 block">Studio Lighting Preset:</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {LIGHTING_PRESETS.map(preset => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleLightingPresetSelect(preset.id)}
                        className={`px-2.5 py-2 rounded-xl text-[11px] font-bold border transition-all cursor-pointer text-left ${
                          lightingPreset === preset.id
                            ? 'bg-amber-500/20 border-amber-500 text-amber-300 shadow-md'
                            : 'bg-zinc-950/60 border-zinc-800/80 text-zinc-400 hover:text-zinc-200'
                        }`}
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lighting Intensity & Ambient Light Controls */}
                <div className="space-y-3 pt-2 border-t border-zinc-800/80">
                  {/* Master Lighting Intensity Slider */}
                  <div className="space-y-1.5 p-2.5 rounded-xl bg-zinc-900/70 border border-zinc-800/80">
                    <div className="flex items-center justify-between text-[11px] font-mono">
                      <div className="flex items-center gap-1.5 text-zinc-200 font-semibold">
                        <Sun className="w-3.5 h-3.5 text-amber-400" />
                        <span>Lighting Intensity</span>
                      </div>
                      <span className="text-amber-300 font-bold px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30">
                        {lightIntensity.toFixed(1)}x
                      </span>
                    </div>
                    <input
                      type="range" min="0.1" max="4.0" step="0.1"
                      value={lightIntensity}
                      onChange={(e) => setLightIntensity(parseFloat(e.target.value))}
                      className="w-full accent-amber-500 cursor-pointer"
                      title="Adjust keylight and specular shine intensity"
                    />
                    <p className="text-[10px] text-zinc-400 leading-tight">
                      Adjusts key spotlight brightness and surface specular highlights on labels.
                    </p>
                  </div>

                  {/* Ambient Light Toggle & Fill Intensity */}
                  <div className="p-2.5 rounded-xl bg-zinc-900/70 border border-zinc-800/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[11px] text-zinc-200 font-semibold">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-400" />
                        <span>Ambient Light (Soft Fill)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAmbientLightEnabled(!ambientLightEnabled)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer border ${
                          ambientLightEnabled 
                            ? 'bg-emerald-600 border-emerald-500' 
                            : 'bg-zinc-800 border-zinc-700'
                        }`}
                        title="Toggle ambient fill lighting on or off"
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            ambientLightEnabled ? 'translate-x-4.5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between text-[10px] font-mono">
                      <span className="text-zinc-400">Environment Fill:</span>
                      <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${
                        ambientLightEnabled 
                          ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/30' 
                          : 'text-amber-300 bg-amber-500/10 border border-amber-500/30'
                      }`}>
                        {ambientLightEnabled ? 'ENABLED (Soft Fill)' : 'MUTED (Deep Contrast)'}
                      </span>
                    </div>

                    {ambientLightEnabled ? (
                      <div className="space-y-1 pt-1.5 border-t border-zinc-800/60">
                        <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                          <span>Ambient Fill Brightness:</span>
                          <span className="text-emerald-300 font-bold">{ambientIntensity.toFixed(2)}</span>
                        </div>
                        <input
                          type="range" min="0.05" max="1.5" step="0.05"
                          value={ambientIntensity}
                          onChange={(e) => setAmbientIntensity(parseFloat(e.target.value))}
                          className="w-full accent-emerald-500 cursor-pointer"
                        />
                      </div>
                    ) : (
                      <p className="text-[10px] text-zinc-400 italic">
                        Ambient fill is off. Yields high-contrast dramatic studio shadows and crisp label relief.
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-[11px] px-1">
                    <span className="text-zinc-400">Spotlight Color:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="w-6 h-6 rounded bg-transparent border border-zinc-700 cursor-pointer"
                      />
                      <span className="font-mono text-zinc-300 text-[10px]">{lightColor}</span>
                    </div>
                  </div>

                  {/* Light Angle Controls */}
                  <div className="grid grid-cols-2 gap-2 text-[10px] pt-1">
                    <div className="space-y-1">
                      <div className="flex justify-between text-zinc-400 font-mono">
                        <span>Light Angle X:</span>
                        <span className="text-amber-300">{lightAngleX}</span>
                      </div>
                      <input
                        type="range" min="-10" max="10" step="1"
                        value={lightAngleX}
                        onChange={(e) => setLightAngleX(parseInt(e.target.value))}
                        className="w-full accent-amber-500 cursor-pointer"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-zinc-400 font-mono">
                        <span>Light Angle Y:</span>
                        <span className="text-amber-300">{lightAngleY}</span>
                      </div>
                      <input
                        type="range" min="2" max="20" step="1"
                        value={lightAngleY}
                        onChange={(e) => setLightAngleY(parseInt(e.target.value))}
                        className="w-full accent-amber-500 cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="space-y-1 pt-1">
                    <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                      <span>Rim Backlight Intensity:</span>
                      <span className="text-purple-300 font-bold">{rimLightIntensity.toFixed(1)}</span>
                    </div>
                    <input
                      type="range" min="0.0" max="3.0" step="0.1"
                      value={rimLightIntensity}
                      onChange={(e) => setRimLightIntensity(parseFloat(e.target.value))}
                      className="w-full accent-purple-500 cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between text-[11px] px-1">
                    <span className="text-zinc-400">Rim Light Color:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={rimLightColor}
                        onChange={(e) => setRimLightColor(e.target.value)}
                        className="w-6 h-6 rounded bg-transparent border border-zinc-700 cursor-pointer"
                      />
                      <span className="font-mono text-zinc-300 text-[10px]">{rimLightColor}</span>
                    </div>
                  </div>

                  {/* Direct Exposure / Brightness Calibration */}
                  <div className="space-y-1 pt-2 border-t border-zinc-800/80">
                    <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                      <span>Camera Tone Exposure:</span>
                      <span className="text-cyan-300 font-bold">{toneMappingExposure.toFixed(2)} EV</span>
                    </div>
                    <input
                      type="range" min="0.4" max="1.8" step="0.05"
                      value={toneMappingExposure}
                      onChange={(e) => setToneMappingExposure(parseFloat(e.target.value))}
                      className="w-full accent-cyan-400 cursor-pointer"
                      title="Direct camera tone exposure level"
                    />
                  </div>

                  {/* Quick Reset Lighting to Balanced Default */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        threeD.update3DState({
                          lightIntensity: 1.8,
                          ambientLightEnabled: true,
                          ambientIntensity: 0.4,
                          rimLightIntensity: 0.8,
                          toneMappingExposure: 0.95,
                          lightColor: '#ffffff'
                        });
                        toast.success('Lighting Balanced', 'Reset studio lighting to calibrated, anti-glare defaults.');
                      }}
                      className="w-full py-1.5 px-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-700/80 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                      title="Reset Keylight, Ambient Fill, and Exposure to balanced photo-realistic defaults"
                    >
                      <RotateCcw className="w-3 h-3 text-amber-400" />
                      <span>Reset to Balanced Studio Lighting</span>
                    </button>
                  </div>
                </div>
              </div>
              )}
            </div>
            )}

            {/* Section 4: Stage & Backdrop Environment */}
            {(activeTab === 'all' || activeTab === 'environment') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('environment')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('environment'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 group-hover:scale-105 transition-transform">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      4. Stage &amp; Environment Backdrops
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">Cyber, Shelf, Marble, Camera Exposure, Pedestal</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.environment ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.environment ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.environment ? (
                    <ChevronDown className="w-4 h-4 text-cyan-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.environment && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                {/* Environment Presets */}
                <div>
                  <label className="text-[11px] font-semibold text-zinc-400 mb-1.5 block">Studio Backdrop Environment:</label>
                  <div className="grid grid-cols-1 gap-1.5">
                    {BACKDROP_ENVIRONMENTS.map(env => (
                      <button
                        key={env.id}
                        type="button"
                        onClick={() => handleBackdropEnvSelect(env.id)}
                        className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                          backdropEnv === env.id
                            ? 'bg-indigo-600/20 border-indigo-500 text-white shadow-md'
                            : 'bg-zinc-950/60 border-zinc-800/80 text-zinc-400 hover:text-zinc-200'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span>{env.icon}</span>
                          <span>{env.name}</span>
                        </span>
                        {backdropEnv === env.id && <CheckCircle2 className="w-4 h-4 text-indigo-400" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tone Mapping Exposure */}
                <div className="space-y-1 pt-2 border-t border-zinc-800/80">
                  <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                    <span>Camera Tone Exposure:</span>
                    <span className="text-cyan-300 font-bold">{toneMappingExposure.toFixed(2)} EV</span>
                  </div>
                  <input
                    type="range" min="0.4" max="2.5" step="0.1"
                    value={toneMappingExposure}
                    onChange={(e) => setToneMappingExposure(parseFloat(e.target.value))}
                    className="w-full accent-cyan-400 cursor-pointer"
                  />
                </div>

                {/* Pedestal Ground Controls */}
                <div className="pt-2 border-t border-zinc-800/80 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-cyan-200">Show 3D Stage Pedestal Ground</span>
                    <input
                      type="checkbox"
                      checked={pedestalVisible}
                      onChange={(e) => setPedestalVisible(e.target.checked)}
                      className="w-4 h-4 accent-cyan-400 cursor-pointer"
                    />
                  </div>

                  {pedestalVisible && (
                    <div className="grid grid-cols-2 gap-2 text-[10px] pt-1">
                      <div>
                        <label className="text-zinc-400 mb-1 block">Pedestal Color:</label>
                        <input
                          type="color"
                          value={pedestalColor}
                          onChange={(e) => setPedestalColor(e.target.value)}
                          className="w-full h-6 rounded bg-transparent border border-zinc-700 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="text-zinc-400 mb-1 block">Glow Ring Color:</label>
                        <input
                          type="color"
                          value={pedestalRingColor}
                          onChange={(e) => setPedestalRingColor(e.target.value)}
                          className="w-full h-6 rounded bg-transparent border border-zinc-700 cursor-pointer"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
            )}

            {/* Section 5: Material Finish & Selective Foil Sheen */}
            {(activeTab === 'all' || activeTab === 'finish') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('finish')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('finish'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 group-hover:scale-105 transition-transform">
                    <Palette className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      5. Finish &amp; Selective Foil Effect
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">Glossy, Matte, Gold/Silver Metallic Foil Tone</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.finish ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.finish ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.finish ? (
                    <ChevronDown className="w-4 h-4 text-amber-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.finish && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                <div className="space-y-1.5">
                  {MATERIAL_FINISHES.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setSelectedFinish(f.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                        selectedFinish === f.id
                          ? 'bg-amber-500/20 border-amber-500 text-amber-300 shadow-md'
                          : 'bg-zinc-950/60 border-zinc-800/80 text-zinc-400 hover:text-zinc-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: f.color || '#38bdf8' }} />
                        <span>{f.name}</span>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500">{f.sheen}</span>
                    </button>
                  ))}
                </div>

                {/* Selective Metallic Foil Color Customization */}
                {(selectedFinish === 'gold_foil' || selectedFinish === 'silver_foil') && (
                  <div className="p-2.5 rounded-xl bg-amber-950/30 border border-amber-800/60 space-y-1.5">
                    <label className="text-[11px] font-bold text-amber-300 flex items-center justify-between">
                      <span>Selective Metallic Foil Tone:</span>
                      <span className="font-mono text-xs">{selectiveFinishColor}</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={selectiveFinishColor}
                        onChange={(e) => setSelectiveFinishColor(e.target.value)}
                        className="w-7 h-7 rounded bg-transparent border border-amber-600 cursor-pointer"
                      />
                      <div className="flex items-center gap-1.5">
                        {['#f59e0b', '#e2e8f0', '#d97706', '#ec4899', '#06b6d4', '#10b981'].map(c => (
                          <button
                            key={c}
                            onClick={() => setSelectiveFinishColor(c)}
                            className="w-4 h-4 rounded-full border border-amber-700 hover:scale-110 transition-transform"
                            style={{ backgroundColor: c }}
                          ></button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Section 6: 3D Tactile Embossing & Raised Spot UV Relief */}
            {(activeTab === 'all' || activeTab === 'finish') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('relief')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('relief'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-400 group-hover:scale-105 transition-transform">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      6. Tactile Embossing &amp; Relief
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">3D Embossed, Debossed, Raised Spot UV, Height</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.relief ? 'bg-purple-500/20 text-purple-300 border-purple-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.relief ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.relief ? (
                    <ChevronDown className="w-4 h-4 text-purple-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.relief && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                  {[
                    { id: 'none', label: 'Flat Print' },
                    { id: 'embossed', label: '3D Embossed (+1mm)' },
                    { id: 'debossed', label: '3D Debossed (-1mm)' },
                    { id: 'spot_uv', label: 'Raised Spot UV' }
                  ].map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => setEmbossMode(mode.id as any)}
                      className={`px-2.5 py-1.5 rounded-lg font-semibold text-[11px] border transition-all cursor-pointer ${
                        embossMode === mode.id
                          ? 'bg-purple-600 text-white border-purple-500 shadow-md'
                          : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>

                {embossMode !== 'none' && (
                  <div className="space-y-3 pt-2 border-t border-zinc-800/60">
                    {/* Tactile Target Area Selection */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-purple-300 flex items-center justify-between">
                        <span>Target Design Area:</span>
                        <span className="text-[10px] font-mono text-purple-400 capitalize">{tactileTargetArea.replace('_', ' ')}</span>
                      </label>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        {[
                          { id: 'all', label: '✨ All Elements' },
                          { id: 'text_logo', label: '🔤 Text & Logos' },
                          { id: 'graphics', label: '📐 Shapes & Icons' },
                          { id: 'high_contrast', label: '✒️ Dark Ink Area' }
                        ].map(area => (
                          <button
                            key={area.id}
                            type="button"
                            onClick={() => setTactileTargetArea(area.id as any)}
                            className={`px-2 py-1.5 rounded-lg font-medium border text-left cursor-pointer transition-all ${
                              tactileTargetArea === area.id
                                ? 'bg-purple-900/50 border-purple-400 text-purple-200 shadow'
                                : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'
                            }`}
                          >
                            {area.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Relief Height Slider */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                        <span>Mesh Height Map Depth:</span>
                        <span className="text-purple-300 font-bold">{reliefHeight.toFixed(1)} mm</span>
                      </div>
                      <input
                        type="range"
                        min="0.2"
                        max="3.0"
                        step="0.1"
                        value={reliefHeight}
                        onChange={(e) => setReliefHeight(parseFloat(e.target.value))}
                        className="w-full accent-purple-500 cursor-pointer"
                      />
                    </div>

                    {/* Tactile Edge Bevel Softness */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
                        <span>Edge Bevel Softness:</span>
                        <span className="text-purple-300 font-bold">{edgeSoftness.toFixed(1)} px</span>
                      </div>
                      <input
                        type="range"
                        min="0.0"
                        max="4.0"
                        step="0.2"
                        value={edgeSoftness}
                        onChange={(e) => setEdgeSoftness(parseFloat(e.target.value))}
                        className="w-full accent-purple-500 cursor-pointer"
                      />
                    </div>

                    {/* Dynamic Shader Height Map Status Indicator */}
                    <div className="p-2 rounded-lg bg-purple-950/40 border border-purple-800/50 flex items-center gap-2 text-[10px] text-purple-300">
                      <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                      <span>
                        Real-Time Shader Height Map: <strong>±{(reliefHeight).toFixed(1)}mm</strong> vertex displacement active on <em>{tactileTargetArea.replace('_', ' ')}</em>.
                      </span>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
            )}

            {/* Section 7: Multi-Label Mode & Condensation */}
            {(activeTab === 'all' || activeTab === 'multilabel') && (
            <div className="rounded-xl border border-zinc-800/90 bg-zinc-950/90 overflow-hidden transition-all shadow-md">
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleSection('multilabel')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection('multilabel'); } }}
                className="w-full flex items-center justify-between p-3 bg-zinc-950 hover:bg-zinc-900/90 transition-colors text-left cursor-pointer group select-none"
              >
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400 group-hover:scale-105 transition-transform">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                      7. Multi-Label &amp; Condensation
                    </h4>
                    <p className="text-[10px] text-zinc-400 font-medium">Back nutrition label, neck seal, water droplets</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                    openSections.multilabel ? 'bg-sky-500/20 text-sky-300 border-sky-500/40' : 'bg-zinc-900 text-zinc-500 border-zinc-800'
                  }`}>
                    {openSections.multilabel ? 'Expanded' : 'Collapsed'}
                  </span>
                  {openSections.multilabel ? (
                    <ChevronDown className="w-4 h-4 text-sky-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>

              {openSections.multilabel && (
              <div className="p-3.5 space-y-3 border-t border-zinc-800/80 bg-zinc-950/50 animate-in fade-in duration-150">
                <div className="flex items-center justify-between border-b border-zinc-800 pb-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                    Enable Secondary Back &amp; Neck Labels
                  </label>
                  <input
                    type="checkbox"
                    checked={enableMultiLabel}
                    onChange={(e) => setEnableMultiLabel(e.target.checked)}
                    className="w-4 h-4 accent-sky-500 cursor-pointer"
                  />
                </div>

                {/* Chilled Condensation Droplets Switch */}
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-cyan-950/30 border border-cyan-800/60">
                  <div className="flex items-center gap-2">
                    <Droplets className="w-4 h-4 text-cyan-300" />
                    <span className="text-xs font-bold text-cyan-200">Chilled Water Droplets</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={showCondensation}
                    onChange={(e) => setShowCondensation(e.target.checked)}
                    className="w-4 h-4 accent-cyan-400 cursor-pointer"
                  />
                </div>

                {enableMultiLabel && (
                  <div className="space-y-2.5 pt-1 text-xs">
                    <div>
                      <label className="text-[11px] font-semibold text-zinc-400 mb-1 block">Back Label Header:</label>
                      <input
                        type="text"
                        value={backLabelTitle}
                        onChange={(e) => setBackLabelTitle(e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 text-white text-xs px-2.5 py-1.5 rounded-lg outline-none font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-zinc-400 mb-1 block">Neck / Seal Ring Text:</label>
                      <input
                        type="text"
                        value={neckLabelText}
                        onChange={(e) => setNeckLabelText(e.target.value)}
                        className="w-full bg-zinc-900 border border-zinc-700 text-white text-xs px-2.5 py-1.5 rounded-lg outline-none font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
            )}

          </div>
        </div>
      </div>

      {/* 360 Spin Frame Gallery Modal */}
      {showSpinGallery && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100000] flex items-center justify-center p-6 animate-in fade-in">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-2xl w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 mr-2">
                  <button 
                    onClick={() => setShowSpinGallery(false)} 
                    className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors cursor-pointer" 
                    title="Close Window" 
                  ></button>
                  <div className="w-3.5 h-3.5 rounded-full bg-yellow-500/50" />
                  <div className="w-3.5 h-3.5 rounded-full bg-green-500/50" />
                </div>
                <Film className="w-5 h-5 text-indigo-400" />
                <h3 className="text-base font-bold text-white">Interactive 360° Spin Animation Player</h3>
              </div>
            </div>

            {spinSnapshots.length > 0 && (
              <div className="space-y-4">
                <div className="w-full h-80 bg-zinc-950 rounded-xl overflow-hidden flex items-center justify-center border border-zinc-800 relative">
                  <img
                    src={spinSnapshots[galleryFrameIndex]}
                    alt="360 Frame"
                    className="max-h-full max-w-full object-contain"
                  />
                  <div className="absolute bottom-3 right-3 bg-black/80 px-2.5 py-1 rounded text-xs font-mono text-zinc-300">
                    Frame {galleryFrameIndex + 1} / {spinSnapshots.length}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-zinc-400 flex items-center justify-between">
                    <span>Rotate 360° Scrub Control:</span>
                    <span className="font-mono text-indigo-400">{Math.round((galleryFrameIndex / spinSnapshots.length) * 360)}°</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max={spinSnapshots.length - 1}
                    value={galleryFrameIndex}
                    onChange={(e) => setGalleryFrameIndex(parseInt(e.target.value))}
                    className="w-full accent-indigo-500 cursor-pointer"
                  />
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-zinc-800">
                  <p className="text-xs text-zinc-400">Scrub slider to inspect packaging from all 360 angles.</p>
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.download = `360-Turntable-Frame-${galleryFrameIndex + 1}.png`;
                      link.href = spinSnapshots[galleryFrameIndex];
                      link.click();
                    }}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold flex items-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Save Current Frame</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Official Printable PDF Proof Certificate Modal */}
      {showPdfProofModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100000] flex items-center justify-center p-6 animate-in fade-in overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-3xl w-full p-6 space-y-5 shadow-2xl relative my-auto">
            {/* Certificate Header */}
            <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 mr-2">
                  <button 
                    onClick={() => setShowPdfProofModal(false)} 
                    className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors cursor-pointer" 
                    title="Close Window" 
                  ></button>
                  <div className="w-3.5 h-3.5 rounded-full bg-yellow-500/50" />
                  <div className="w-3.5 h-3.5 rounded-full bg-green-500/50" />
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-xl shadow-lg">
                  ✓
                </div>
                <div>
                  <h2 className="text-lg font-extrabold text-white">Official 3D Packaging Print Proof</h2>
                  <p className="text-xs text-zinc-400 font-mono">Document Ref: PRF-2026-X880 • Date: {new Date().toLocaleDateString()}</p>
                </div>
              </div>
              <div className="text-right">
                <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                  {approvalStatus}
                </span>
              </div>
            </div>

            {/* Certificate Body Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 3D Render Snapshot */}
              <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800 flex flex-col items-center justify-center relative">
                <img
                  src={proofSnapshotUrl}
                  alt="3D Proof"
                  className="max-h-56 object-contain rounded-lg"
                />
                <span className="text-[10px] text-zinc-500 font-mono mt-2">Verified High-Res 3D Packaging Render</span>
              </div>

              {/* Technical Specifications */}
              <div className="space-y-3 bg-zinc-950 p-4 rounded-xl border border-zinc-800 text-xs">
                <h4 className="font-bold text-white uppercase tracking-wider text-[11px] border-b border-zinc-800 pb-1 text-emerald-400">Technical Production Specs</h4>
                <div className="space-y-1.5 font-mono text-zinc-300 text-[11px]">
                  <div className="flex justify-between"><span>Substrate:</span><strong className="text-white">{selectedModel.toUpperCase()}</strong></div>
                  <div className="flex justify-between"><span>Container Finish:</span><strong className="text-white">{containerMaterial.toUpperCase()}</strong></div>
                  <div className="flex justify-between"><span>Label Finish:</span><strong className="text-white">{selectedFinish.toUpperCase()}</strong></div>
                  <div className="flex justify-between"><span>Tactile Embossing:</span><strong className="text-white">{embossMode.toUpperCase()} ({reliefHeight}mm)</strong></div>
                  <div className="flex justify-between"><span>Multi-Label Layout:</span><strong className="text-white">{enableMultiLabel ? 'Front + Back + Neck' : 'Single Label'}</strong></div>
                  <div className="flex justify-between"><span>Dimensions:</span><strong className="text-white">{template.width}mm × {template.height}mm</strong></div>
                </div>

                {/* QR Code Section */}
                <div className="pt-2 border-t border-zinc-800 flex items-center gap-3">
                  {proofQrCodeUrl && (
                    <img src={proofQrCodeUrl} alt="QR Code" className="w-16 h-16 rounded bg-white p-1" />
                  )}
                  <div>
                    <span className="text-[11px] font-bold text-white block">Scan to View Interactive 3D</span>
                    <span className="text-[10px] text-zinc-400 block">Client AR/3D Web Mobile Link</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Client Notes & Approval Signoff */}
            <div className="bg-zinc-950 p-3.5 rounded-xl border border-zinc-800 text-xs space-y-1">
              <span className="font-bold text-zinc-300">Client Sign-Off Notes:</span>
              <p className="text-zinc-400 font-mono italic">{clientNotes || 'No notes specified.'}</p>
            </div>

            {/* Certificate Footer Controls */}
            <div className="flex justify-between items-center pt-2">
              <button
                onClick={() => setShowPdfProofModal(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold"
              >
                Close Certificate
              </button>
              <button
                onClick={() => window.print()}
                className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-lg"
              >
                <Printer className="w-4 h-4" />
                <span>Print / Save PDF Proof</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </MacWindow>
  );
};

export { ThreeDView } from './ThreeDView';
